import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageSquare, QrCode, Loader2, Send, Search, ArrowLeft, Settings2, Phone, RefreshCw, LogOut, CheckCircle2, Clock, Bell, Save, X, User as UserIcon, Users, Check, CheckCheck, UserPlus, Edit2, MapPin, Mail, Calendar, Briefcase, Tag, Cake, Plus, Trash2, Info, Upload, BarChart3, Eye, MessageCircle, Heart, ChevronDown, ChevronRight, Radio, Hash, Link2, FileText } from 'lucide-react';
import { supabase } from '../supabase';
import { CONFIG } from '../config';
import { User, Container, Contact, WhatsAppGroupHub, WhatsAppGroupHubMember, WhatsAppGroupMessage, WhatsAppBroadcastList, WhatsAppBroadcastMessage, Task, Attendance } from '../types';

interface WhatsAppPanelProps {
  currentUser: User;
  container: Container | null;
  contacts?: Contact[];
  tasks?: Task[];
  attendances?: Attendance[];
  onCreateContact?: (phone?: string, name?: string, photoUrl?: string) => void;
  onViewContact?: (contactId: string) => void;
  onCreateForContact?: (type: 'task' | 'attendance' | 'document', contactId: string) => void;
  subView?: 'main' | 'settings' | 'birthdays' | 'flows' | 'groups' | 'broadcasts';
  onNavigate?: (tab: string) => void;
}

interface Chat {
  id: string;
  name: string;
  lastMessage?: { body?: string; timestamp?: number };
  picture?: string;
  unreadCount?: number;
}

interface Message {
  id: string;
  body: string;
  fromMe: boolean;
  timestamp: number;
  from?: string;
  to?: string;
  ack?: number;
  participant?: string;
  hasMedia?: boolean;
  type?: string;
  mediaUrl?: string;
  media?: { mimetype?: string; url?: string; filename?: string };
  _data?: {
    pushName?: string;
    key?: { participantAlt?: string };
    mimetype?: string;
    caption?: string;
    type?: string;
  };
}

interface ReminderConfig {
  hours_before: number;
  notify_on_due_day: boolean;
  notify_completed_on_deadline: boolean;
  active: boolean;
  reminder_hour: number;
  reminder_minute: number;
  remind_on_due_day: boolean;
  remind_day_before: boolean;
  remind_week_before: boolean;
  notify_task_created: boolean;
  notify_task_assigned: boolean;
  notify_status_changed: boolean;
  notify_task_completed: boolean;
  notify_comment_added: boolean;
  notify_task_created_resp: boolean;
  notify_task_created_stake: boolean;
  notify_task_assigned_resp: boolean;
  notify_task_assigned_stake: boolean;
  notify_status_changed_resp: boolean;
  notify_status_changed_stake: boolean;
  notify_task_completed_resp: boolean;
  notify_task_completed_stake: boolean;
  notify_comment_added_resp: boolean;
  notify_comment_added_stake: boolean;
}

interface BirthdayMessage {
  id: string;
  type: 'text' | 'image' | 'audio' | 'video';
  content: string;
  fileName?: string;
  intervalMin?: number;
  intervalMax?: number;
  showPresence?: boolean;
  presenceType?: 'typing' | 'recording';
}

interface BirthdayConfig {
  active: boolean;
  startHour: number;
  startMinute: number;
  messages: BirthdayMessage[];
  msgIntervalMin: number;
  msgIntervalMax: number;
  recipientIntervalMin: number;
  recipientIntervalMax: number;
  dailyLimit: number;
}

interface WhatsAppFlow {
  id: string;
  container_id: string;
  name: string;
  messages: BirthdayMessage[];
  target_tags: string[];
  status: 'draft' | 'sending' | 'completed' | 'paused';
  created_at: string;
  updated_at: string;
}

type ChatTab = 'all' | 'contacts' | 'groups';

const WhatsAppPanel: React.FC<WhatsAppPanelProps> = ({ currentUser, container, contacts: crmContacts = [], tasks: allTasks = [], attendances: allAttendances = [], onCreateContact, onViewContact, onCreateForContact, subView = 'main', onNavigate }) => {
  const [sessionStatus, setSessionStatus] = useState<string>('disconnected');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [waEngine, setWaEngine] = useState<'NOWEB' | 'WEBJS' | 'GOWS'>('GOWS');
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
  const [qrCode, setQrCode] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const [chats, setChats] = useState<Chat[]>([]);
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [messageText, setMessageText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [chatTab, setChatTab] = useState<ChatTab>('all');
  const [loadingChats, setLoadingChats] = useState(false);
  const [syncingChats, setSyncingChats] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);

  const [reminderConfig, setReminderConfig] = useState<ReminderConfig>({
    hours_before: 24, notify_on_due_day: true, notify_completed_on_deadline: false, active: true,
    reminder_hour: 11, reminder_minute: 0, remind_on_due_day: true, remind_day_before: true, remind_week_before: false,
    notify_task_created: true, notify_task_assigned: true, notify_status_changed: true, notify_task_completed: true, notify_comment_added: true,
    notify_task_created_resp: true, notify_task_created_stake: true,
    notify_task_assigned_resp: true, notify_task_assigned_stake: true,
    notify_status_changed_resp: true, notify_status_changed_stake: true,
    notify_task_completed_resp: true, notify_task_completed_stake: true,
    notify_comment_added_resp: true, notify_comment_added_stake: true,
  });
  const [savingConfig, setSavingConfig] = useState(false);
  const [configSaved, setConfigSaved] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [birthdayConfig, setBirthdayConfig] = useState<BirthdayConfig>({
    active: false, startHour: 9, startMinute: 0,
    messages: [{ id: '1', type: 'text', content: '🎂 Feliz aniversário, {nome}! Desejamos a você um dia repleto de alegrias. Um abraço da equipe do gabinete! 🎉' }],
    msgIntervalMin: 30, msgIntervalMax: 60,
    recipientIntervalMin: 2, recipientIntervalMax: 5, dailyLimit: 20,
  });
  const [savingBirthday, setSavingBirthday] = useState(false);
  const [birthdaySaved, setBirthdaySaved] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState<string | null>(null);
  const [flows, setFlows] = useState<WhatsAppFlow[]>([]);
  const [editingFlow, setEditingFlow] = useState<WhatsAppFlow | null>(null);
  const [flowMessages, setFlowMessages] = useState<BirthdayMessage[]>([{ id: '1', type: 'text', content: '' }]);
  const [flowName, setFlowName] = useState('');
  const [flowTags, setFlowTags] = useState<string[]>([]);
  const [savingFlow, setSavingFlow] = useState(false);
  const [loadingFlows, setLoadingFlows] = useState(false);
  const [sendingFlowId, setSendingFlowId] = useState<string | null>(null);
  const [showFlowPicker, setShowFlowPicker] = useState(false);

  // Groups management
  const [groupHubs, setGroupHubs] = useState<WhatsAppGroupHub[]>([]);
  const [groupHubMembers, setGroupHubMembers] = useState<Record<string, WhatsAppGroupHubMember[]>>({});
  const [groupMessages, setGroupMessages] = useState<WhatsAppGroupMessage[]>([]);
  const [loadingGroupHubs, setLoadingGroupHubs] = useState(false);
  const [editingHub, setEditingHub] = useState<WhatsAppGroupHub | null>(null);
  const [hubName, setHubName] = useState('');
  const [hubDescription, setHubDescription] = useState('');
  const [savingHub, setSavingHub] = useState(false);
  const [selectedGroupChat, setSelectedGroupChat] = useState<Chat | null>(null);
  const [groupInfo, setGroupInfo] = useState<any>(null);
  const [loadingGroupInfo, setLoadingGroupInfo] = useState(false);
  const [sendingHubFlow, setSendingHubFlow] = useState(false);
  const [selectedHubId, setSelectedHubId] = useState<string | null>(null);
  const [hubFlowMessages, setHubFlowMessages] = useState<BirthdayMessage[]>([{ id: '1', type: 'text', content: '' }]);
  const [uploadingHubMedia, setUploadingHubMedia] = useState<string | null>(null);
  const [savingContactPhoto, setSavingContactPhoto] = useState(false);

  // Broadcast monitor
  const [broadcastChats, setBroadcastChats] = useState<{ id: string; name: string; lastMessage?: any; timestamp?: number }[]>([]);
  const [broadcastData, setBroadcastData] = useState<{ totalRecipients: number; allRecipients: { phone: string; name: string }[]; messages: { baseId: string; body: string; timestamp: number; aggregateAck: number; recipients: { phone: string; name: string; ack: number; ackName: string }[] }[] } | null>(null);
  const [loadingBroadcasts, setLoadingBroadcasts] = useState(false);
  const [selectedBroadcastId, setSelectedBroadcastId] = useState<string | null>(null);
  const [loadingBroadcastMsgs, setLoadingBroadcastMsgs] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const msgPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ─── API helpers ───
  const getAuthHeaders = useCallback(async () => {
    const session = (await supabase.auth.getSession()).data.session;
    if (!session) throw new Error('No session');
    return { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json', 'apikey': CONFIG.supabaseAnonKey };
  }, []);

  const edgeFn = useCallback(async (action: string, options?: { method?: string; body?: any; params?: Record<string, string> }) => {
    const headers = await getAuthHeaders();
    const params = new URLSearchParams({ action, ...(options?.params || {}) });
    const res = await fetch(`${CONFIG.supabaseUrl}/functions/v1/whatsapp-session?${params}`, {
      method: options?.method || 'GET', headers,
      ...(options?.body ? { body: JSON.stringify(options.body) } : {}),
    });
    return res.json();
  }, [getAuthHeaders]);

  const fetchQrCode = useCallback(async () => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${CONFIG.supabaseUrl}/functions/v1/whatsapp-session?action=qr`, { headers });
      if (!res.ok) return;
      const blob = await res.blob();
      setQrCode(URL.createObjectURL(blob));
    } catch {}
  }, [getAuthHeaders]);

  const checkStatus = useCallback(async () => {
    try {
      const data = await edgeFn('status');
      console.log('[WhatsApp] checkStatus response:', data);
      setSessionStatus(data.status || 'disconnected');
      if (data.phone_number) setPhoneNumber(data.phone_number);
      if (data.engine) setWaEngine(data.engine);
      return data.status;
    } catch (e: any) { console.error('[WhatsApp] checkStatus error:', e); return 'disconnected'; }
  }, [edgeFn]);

  const syncContacts = useCallback(async () => {
    try { await edgeFn('sync-contacts', { method: 'POST' }); } catch {}
  }, [edgeFn]);

  const handleAddToCRM = useCallback(async (chat: Chat, phone: string) => {
    setSavingContactPhoto(true);
    const masked = !chat.name || chat.name.includes('∙') || /^\+\d{2}[∙.·•]+/.test(chat.name);
    const name = chat.name && !masked ? chat.name : undefined;
    let photoUrl: string | undefined;
    try {
      const res = await edgeFn('profile-picture', { params: { contactId: chat.id } });
      if (res?.photoUrl) photoUrl = res.photoUrl;
    } catch {}
    setSavingContactPhoto(false);
    onCreateContact?.(phone, name, photoUrl);
  }, [edgeFn, onCreateContact]);

  const fetchChats = useCallback(async (retries = 0): Promise<boolean> => {
    setLoadingChats(true);
    try {
      const data = await edgeFn('chats');
      if (Array.isArray(data)) {
        const oneMonthAgo = Math.floor((Date.now() - 30 * 86400000) / 1000);
        const sorted = data
          .filter((c: any) => c.id && !c.id.includes('@newsletter') && !c.id.includes('@lid') && c.id !== 'status@broadcast')
          .filter((c: any) => !c.lastMessage?.timestamp || c.lastMessage.timestamp >= oneMonthAgo)
          .sort((a: any, b: any) => (b.lastMessage?.timestamp || 0) - (a.lastMessage?.timestamp || 0));
        setChats(sorted);
        setLoadingChats(false);
        if (sorted.length > 0) { setSyncingChats(false); return true; }
      }
    } catch (e) { console.error('Failed to fetch chats', e); }
    setLoadingChats(false);
    if (retries < 10) {
      setSyncingChats(true);
      await new Promise(r => setTimeout(r, 3000));
      return fetchChats(retries + 1);
    }
    setSyncingChats(false);
    return false;
  }, [edgeFn]);

  const fetchMessages = useCallback(async (chatId: string) => {
    setLoadingMessages(true);
    try {
      const data = await edgeFn('messages', { params: { chatId, limit: '50' } });
      if (Array.isArray(data)) {
        const sorted = data.sort((a: any, b: any) => a.timestamp - b.timestamp);
        const key = (m: any) => `${m.timestamp}_${m.fromMe}_${(m.body || '').slice(0, 20)}`;
        const deduped = sorted.filter((m: any, i: number, arr: any[]) => i === arr.findIndex((x: any) => key(x) === key(m)));
        setMessages(deduped);
      }
    } catch (e) { console.error('Failed to fetch messages', e); }
    setLoadingMessages(false);
  }, [edgeFn]);

  const fetchReminderConfig = useCallback(async () => {
    if (!container?.id) return;
    const { data } = await supabase.from('whatsapp_reminder_configs').select('*').eq('container_id', container.id).single();
    if (data) {
      setReminderConfig(data);
      setBirthdayConfig({
        active: data.birthday_active ?? false,
        startHour: data.birthday_start_hour ?? 9,
        startMinute: data.birthday_start_minute ?? 0,
        messages: data.birthday_messages ?? [{ id: '1', type: 'text', content: '🎂 Feliz aniversário, {nome}! Desejamos a você um dia repleto de alegrias. Um abraço da equipe do gabinete! 🎉' }],
        msgIntervalMin: data.birthday_msg_interval_min ?? 30,
        msgIntervalMax: data.birthday_msg_interval_max ?? 60,
        recipientIntervalMin: data.birthday_recipient_interval_min ?? 2,
        recipientIntervalMax: data.birthday_recipient_interval_max ?? 5,
        dailyLimit: data.birthday_daily_limit ?? 20,
      });
    }
  }, [container?.id]);

  // ─── Lifecycle ───
  useEffect(() => {
    (async () => {
      let hasCache = false;
      if (container?.id) {
        try {
          const cached = JSON.parse(sessionStorage.getItem(`wa_${container.id}`) || 'null');
          if (cached?.status === 'connected' && cached.chats?.length > 0) {
            setSessionStatus('connected');
            setPhoneNumber(cached.phone || '');
            setChats(cached.chats);
            hasCache = true;
          }
        } catch {}
      }
      if (!hasCache) setLoading(true);
      const status = await checkStatus();
      if (status === 'connected') {
        syncContacts(); await fetchChats();
        edgeFn('sync-cache', { method: 'POST' }).catch(() => {});
        edgeFn('unread').then((data: any) => { if (Array.isArray(data)) { const map: Record<string, number> = {}; data.forEach((u: any) => { map[u.chat_id] = u.count }); setUnreadCounts(map); } }).catch(() => {});
      }
      setLoading(false);
    })();
  }, []);

  // Auto-save cache
  useEffect(() => {
    if (!container?.id) return;
    if (sessionStatus === 'connected' && chats.length > 0) {
      try { sessionStorage.setItem(`wa_${container.id}`, JSON.stringify({ status: sessionStatus, phone: phoneNumber, chats: chats.slice(0, 50) })); } catch {}
    } else if (sessionStatus !== 'connected' && sessionStatus !== 'disconnected') {
      // Don't clear on initial default 'disconnected'
    } else if (sessionStatus === 'disconnected' && !loading) {
      sessionStorage.removeItem(`wa_${container.id}`);
    }
  }, [sessionStatus, phoneNumber, chats, container?.id, loading]);

  // Lazy load config when settings or birthdays view opens
  useEffect(() => {
    if ((subView === 'settings' || subView === 'birthdays') && !configLoaded) {
      fetchReminderConfig().then(() => setConfigLoaded(true));
    }
  }, [subView, configLoaded]);

  useEffect(() => {
    if (sessionStatus === 'qr_pending' || sessionStatus === 'connecting') {
      pollRef.current = setInterval(async () => {
        const status = await checkStatus();
        if (status === 'connected') { if (pollRef.current) clearInterval(pollRef.current); syncContacts(); await fetchChats(); }
        else if (status === 'qr_pending') await fetchQrCode();
      }, 3000);
    } else { if (pollRef.current) clearInterval(pollRef.current); }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [sessionStatus]);

  // Poll unread counts + refresh chat list
  useEffect(() => {
    if (sessionStatus !== 'connected') return;
    const interval = setInterval(() => {
      edgeFn('unread').then((data: any) => { if (Array.isArray(data)) { const map: Record<string, number> = {}; data.forEach((u: any) => { map[u.chat_id] = u.count }); setUnreadCounts(map); } }).catch(() => {});
      if (!selectedChat) {
        // Silent refresh - don't show loading spinner
        edgeFn('chats').then((data: any) => {
          if (Array.isArray(data)) {
            const oneMonthAgo = Math.floor((Date.now() - 30 * 86400000) / 1000);
            const sorted = data
              .filter((c: any) => c.id && !c.id.includes('@newsletter') && !c.id.includes('@lid') && c.id !== 'status@broadcast')
              .filter((c: any) => !c.lastMessage?.timestamp || c.lastMessage.timestamp >= oneMonthAgo)
              .sort((a: any, b: any) => (b.lastMessage?.timestamp || 0) - (a.lastMessage?.timestamp || 0));
            setChats(sorted);
          }
        }).catch(() => {});
      }
    }, 15000);
    return () => clearInterval(interval);
  }, [sessionStatus, edgeFn, selectedChat]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  useEffect(() => {
    if (msgPollRef.current) clearInterval(msgPollRef.current);
    if (selectedChat && sessionStatus === 'connected') {
      msgPollRef.current = setInterval(async () => {
        try {
          const data = await edgeFn('messages', { params: { chatId: selectedChat.id, limit: '50' } });
          if (Array.isArray(data)) {
            const sorted = data.sort((a: any, b: any) => a.timestamp - b.timestamp);
            const key = (m: any) => `${m.timestamp}_${m.fromMe}_${(m.body || '').slice(0, 20)}`;
            const deduped = sorted.filter((m: any, i: number, arr: any[]) => i === arr.findIndex((x: any) => key(x) === key(m)));
            setMessages(prev => {
              const prevKey = prev.map(key).join('|');
              const newKey = deduped.map(key).join('|');
              return prevKey !== newKey ? deduped : prev;
            });
          }
        } catch {}
      }, 5000);
    }
    return () => { if (msgPollRef.current) clearInterval(msgPollRef.current); };
  }, [selectedChat?.id, sessionStatus]);

  // ─── Actions ───
  const handleStart = async () => {
    setActionLoading(true);
    try { await edgeFn('start', { method: 'POST' }); setSessionStatus('connecting'); } catch (e) { console.error(e); }
    setActionLoading(false);
  };

  const handleDisconnect = async () => {
    setActionLoading(true);
    try { await edgeFn('logout', { method: 'POST' }); setSessionStatus('disconnected'); setPhoneNumber(''); setChats([]); setSelectedChat(null); setMessages([]); setQrCode(''); } catch (e) { console.error(e); }
    setActionLoading(false);
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedChat) return;
    setSendingMessage(true);
    try {
      await edgeFn('send', { method: 'POST', body: { chatId: selectedChat.id, text: messageText } });
      setMessages(prev => [...prev, { id: `local_${Date.now()}`, body: messageText, fromMe: true, timestamp: Math.floor(Date.now() / 1000), ack: 1 }]);
      setMessageText('');
    } catch (e) { console.error(e); }
    setSendingMessage(false);
  };

  const handleSaveConfig = async () => {
    if (!container?.id) return;
    setSavingConfig(true);
    try {
      await supabase.from('whatsapp_reminder_configs').upsert({
        container_id: container.id,
        active: reminderConfig.active,
        hours_before: reminderConfig.hours_before,
        notify_on_due_day: reminderConfig.notify_on_due_day,
        notify_completed_on_deadline: reminderConfig.notify_completed_on_deadline,
        reminder_hour: reminderConfig.reminder_hour,
        reminder_minute: reminderConfig.reminder_minute,
        remind_on_due_day: reminderConfig.remind_on_due_day,
        remind_day_before: reminderConfig.remind_day_before,
        remind_week_before: reminderConfig.remind_week_before,
        notify_task_created: reminderConfig.notify_task_created,
        notify_task_assigned: reminderConfig.notify_task_assigned,
        notify_status_changed: reminderConfig.notify_status_changed,
        notify_task_completed: reminderConfig.notify_task_completed,
        notify_comment_added: reminderConfig.notify_comment_added,
        notify_task_created_resp: reminderConfig.notify_task_created_resp,
        notify_task_created_stake: reminderConfig.notify_task_created_stake,
        notify_task_assigned_resp: reminderConfig.notify_task_assigned_resp,
        notify_task_assigned_stake: reminderConfig.notify_task_assigned_stake,
        notify_status_changed_resp: reminderConfig.notify_status_changed_resp,
        notify_status_changed_stake: reminderConfig.notify_status_changed_stake,
        notify_task_completed_resp: reminderConfig.notify_task_completed_resp,
        notify_task_completed_stake: reminderConfig.notify_task_completed_stake,
        notify_comment_added_resp: reminderConfig.notify_comment_added_resp,
        notify_comment_added_stake: reminderConfig.notify_comment_added_stake,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'container_id' });
      setConfigSaved(true);
      setTimeout(() => setConfigSaved(false), 2000);
    } catch (e) { console.error(e); }
    setSavingConfig(false);
  };

  const handleSaveBirthdayConfig = async () => {
    if (!container?.id) return;
    setSavingBirthday(true);
    try {
      await supabase.from('whatsapp_reminder_configs').upsert({
        container_id: container.id,
        birthday_active: birthdayConfig.active,
        birthday_start_hour: birthdayConfig.startHour,
        birthday_start_minute: birthdayConfig.startMinute,
        birthday_messages: birthdayConfig.messages,
        birthday_msg_interval_min: birthdayConfig.msgIntervalMin,
        birthday_msg_interval_max: birthdayConfig.msgIntervalMax,
        birthday_recipient_interval_min: birthdayConfig.recipientIntervalMin,
        birthday_recipient_interval_max: birthdayConfig.recipientIntervalMax,
        birthday_daily_limit: birthdayConfig.dailyLimit,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'container_id' });
      setBirthdaySaved(true);
      setTimeout(() => setBirthdaySaved(false), 2000);
    } catch (e) { console.error(e); }
    setSavingBirthday(false);
  };

  // ─── Flows ───
  const fetchFlows = useCallback(async () => {
    if (!container?.id) return;
    setLoadingFlows(true);
    const { data } = await supabase.from('whatsapp_flows').select('*').eq('container_id', container.id).order('updated_at', { ascending: false });
    setFlows(data || []);
    setLoadingFlows(false);
  }, [container?.id]);

  useEffect(() => { if (subView === 'flows') fetchFlows(); }, [subView, fetchFlows]);

  const handleSaveFlow = async () => {
    if (!container?.id || !flowName.trim()) return;
    setSavingFlow(true);
    try {
      if (editingFlow) {
        await supabase.from('whatsapp_flows').update({ name: flowName, messages: flowMessages, target_tags: flowTags, updated_at: new Date().toISOString() }).eq('id', editingFlow.id);
      } else {
        await supabase.from('whatsapp_flows').insert({ container_id: container.id, name: flowName, messages: flowMessages, target_tags: flowTags });
      }
      setEditingFlow(null);
      setFlowName('');
      setFlowMessages([{ id: '1', type: 'text', content: '' }]);
      setFlowTags([]);
      fetchFlows();
    } catch (e) { console.error(e); }
    setSavingFlow(false);
  };

  const handleDeleteFlow = async (id: string) => {
    await supabase.from('whatsapp_flows').delete().eq('id', id);
    fetchFlows();
  };

  const handleSendFlow = async (flow: WhatsAppFlow, chatId?: string) => {
    if (!container?.id) return;
    setSendingFlowId(flow.id);
    try {
      const headers = await getAuthHeaders();
      const targets: { chatId: string; contactId?: string; name?: string }[] = [];

      if (chatId) {
        targets.push({ chatId });
      } else {
        const filteredContacts = (crmContacts || []).filter(c =>
          (c.phones || [])[0] && !c.deleted_at && (flow.target_tags.length === 0 || flow.target_tags.some(tag => (c.tags || []).includes(tag)))
        );
        for (const c of filteredContacts) {
          targets.push({ chatId: `${(c.phones[0] || '').replace(/\D/g, '')}@s.whatsapp.net`, contactId: c.id, name: c.name });
        }
      }

      for (const target of targets) {
        for (let i = 0; i < flow.messages.length; i++) {
          const msg = flow.messages[i];
          if (i > 0) {
            const min = msg.intervalMin ?? 10;
            const max = msg.intervalMax ?? 20;
            const delay = min + Math.random() * (max - min);
            await new Promise(r => setTimeout(r, delay * 1000));
          }
          if (msg.showPresence) {
            const presenceType = msg.presenceType || (msg.type === 'audio' ? 'recording' : 'typing');
            await fetch(`${CONFIG.supabaseUrl}/functions/v1/whatsapp-session?action=presence`, { method: 'POST', headers, body: JSON.stringify({ chatId: target.chatId, presence: presenceType }) }).catch(() => {});
            await new Promise(r => setTimeout(r, 3000));
          }
          const name = target.name || '';
          if (msg.type === 'text') {
            await fetch(`${CONFIG.supabaseUrl}/functions/v1/whatsapp-session?action=send`, { method: 'POST', headers, body: JSON.stringify({ chatId: target.chatId, text: msg.content.replace(/\{nome\}/g, name) }) });
          } else if (msg.type === 'audio') {
            await fetch(`${CONFIG.supabaseUrl}/functions/v1/whatsapp-session?action=send-voice`, { method: 'POST', headers, body: JSON.stringify({ chatId: target.chatId, fileUrl: msg.content }) });
          } else if (msg.type === 'image') {
            await fetch(`${CONFIG.supabaseUrl}/functions/v1/whatsapp-session?action=send-image`, { method: 'POST', headers, body: JSON.stringify({ chatId: target.chatId, fileUrl: msg.content }) });
          } else if (msg.type === 'video') {
            await fetch(`${CONFIG.supabaseUrl}/functions/v1/whatsapp-session?action=send-video`, { method: 'POST', headers, body: JSON.stringify({ chatId: target.chatId, fileUrl: msg.content }) });
          }
        }
        if (target.contactId) {
          await supabase.from('whatsapp_flow_sent').insert({ container_id: container.id, flow_id: flow.id, contact_id: target.contactId, chat_id: target.chatId, status: 'completed' });
        }
      }
    } catch (e) { console.error(e); }
    setSendingFlowId(null);
  };

  // ─── Groups ───
  const fetchGroupHubs = useCallback(async () => {
    if (!container?.id) return;
    setLoadingGroupHubs(true);
    const { data } = await supabase.from('whatsapp_group_hubs').select('*').eq('container_id', container.id).order('updated_at', { ascending: false });
    setGroupHubs(data || []);
    if (data) {
      const memberMap: Record<string, WhatsAppGroupHubMember[]> = {};
      for (const hub of data) {
        const { data: members } = await supabase.from('whatsapp_group_hub_members').select('*').eq('hub_id', hub.id);
        memberMap[hub.id] = members || [];
      }
      setGroupHubMembers(memberMap);
    }
    setLoadingGroupHubs(false);
  }, [container?.id]);

  const fetchGroupMessages = useCallback(async () => {
    if (!container?.id) return;
    const { data } = await supabase.from('whatsapp_group_messages').select('*').eq('container_id', container.id).order('sent_at', { ascending: false }).limit(50);
    setGroupMessages(data || []);
  }, [container?.id]);

  const fetchGroupInfo = useCallback(async (groupJid: string) => {
    setLoadingGroupInfo(true);
    try {
      const data = await edgeFn('group-info', { params: { groupId: groupJid } });
      setGroupInfo(data);
    } catch { setGroupInfo(null); }
    setLoadingGroupInfo(false);
  }, [edgeFn]);

  useEffect(() => {
    if (subView === 'groups' && sessionStatus === 'connected') {
      fetchGroupHubs();
      fetchGroupMessages();
    }
  }, [subView, sessionStatus]);

  const handleSaveHub = async () => {
    if (!container?.id || !hubName.trim()) return;
    setSavingHub(true);
    try {
      if (editingHub) {
        await supabase.from('whatsapp_group_hubs').update({ name: hubName, description: hubDescription, updated_at: new Date().toISOString() }).eq('id', editingHub.id);
      } else {
        await supabase.from('whatsapp_group_hubs').insert({ container_id: container.id, name: hubName, description: hubDescription, created_by: currentUser.id });
      }
      setEditingHub(null);
      setHubName('');
      setHubDescription('');
      fetchGroupHubs();
    } catch (e) { console.error(e); }
    setSavingHub(false);
  };

  const handleAddGroupToHub = async (hubId: string, groupJid: string, groupName: string) => {
    await supabase.from('whatsapp_group_hub_members').upsert({ hub_id: hubId, group_jid: groupJid, group_name: groupName }, { onConflict: 'hub_id,group_jid' });
    fetchGroupHubs();
  };

  const handleRemoveGroupFromHub = async (hubId: string, groupJid: string) => {
    await supabase.from('whatsapp_group_hub_members').delete().eq('hub_id', hubId).eq('group_jid', groupJid);
    fetchGroupHubs();
  };

  const handleSendFlowToHub = async (hubId: string) => {
    if (!container?.id || hubFlowMessages.every(m => !m.content.trim())) return;
    setSendingHubFlow(true);
    try {
      const headers = await getAuthHeaders();
      const members = groupHubMembers[hubId] || [];
      for (const member of members) {
        for (let i = 0; i < hubFlowMessages.length; i++) {
          const msg = hubFlowMessages[i];
          if (i > 0) {
            const min = msg.intervalMin ?? 10;
            const max = msg.intervalMax ?? 20;
            await new Promise(r => setTimeout(r, (min + Math.random() * (max - min)) * 1000));
          }
          if (msg.showPresence) {
            const presenceType = msg.presenceType || (msg.type === 'audio' ? 'recording' : 'typing');
            await fetch(`${CONFIG.supabaseUrl}/functions/v1/whatsapp-session?action=presence`, { method: 'POST', headers, body: JSON.stringify({ chatId: member.group_jid, presence: presenceType }) }).catch(() => {});
            await new Promise(r => setTimeout(r, 3000));
          }
          if (msg.type === 'text') {
            await edgeFn('send', { method: 'POST', body: { chatId: member.group_jid, text: msg.content } });
          } else if (msg.type === 'audio') {
            await fetch(`${CONFIG.supabaseUrl}/functions/v1/whatsapp-session?action=send-voice`, { method: 'POST', headers, body: JSON.stringify({ chatId: member.group_jid, fileUrl: msg.content }) });
          } else if (msg.type === 'image') {
            await fetch(`${CONFIG.supabaseUrl}/functions/v1/whatsapp-session?action=send-image`, { method: 'POST', headers, body: JSON.stringify({ chatId: member.group_jid, fileUrl: msg.content }) });
          } else if (msg.type === 'video') {
            await fetch(`${CONFIG.supabaseUrl}/functions/v1/whatsapp-session?action=send-video`, { method: 'POST', headers, body: JSON.stringify({ chatId: member.group_jid, fileUrl: msg.content }) });
          }
        }
      }
      await supabase.from('whatsapp_group_messages').insert({
        container_id: container.id, hub_id: hubId,
        message_content: { type: 'flow', messages: hubFlowMessages },
        sent_by: currentUser.id, total_groups: members.length, total_participants: 0,
      });
      setHubFlowMessages([{ id: '1', type: 'text', content: '' }]);
      fetchGroupMessages();
    } catch (e) { console.error(e); }
    setSendingHubFlow(false);
  };

  const hubFlowUpdateMessage = (id: string, updates: Partial<BirthdayMessage>) => {
    setHubFlowMessages(prev => prev.map(m => m.id === id ? { ...m, ...updates } : m));
  };
  const hubFlowAddMessage = () => {
    setHubFlowMessages(prev => [...prev, { id: String(Date.now()), type: 'text' as const, content: '', intervalMin: 10, intervalMax: 20, showPresence: false, presenceType: 'typing' as const }]);
  };
  const hubFlowRemoveMessage = (id: string) => {
    if (hubFlowMessages.length <= 1) return;
    setHubFlowMessages(prev => prev.filter(m => m.id !== id));
  };
  const hubFlowHandleMediaUpload = async (msgId: string, file: File) => {
    setUploadingHubMedia(msgId);
    try {
      const ext = file.name.split('.').pop();
      const path = `hub-flows/${container?.id}/${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from('whatsapp-media').upload(path, file);
      if (error) { setUploadingHubMedia(null); return; }
      const { data: { publicUrl } } = supabase.storage.from('whatsapp-media').getPublicUrl(path);
      hubFlowUpdateMessage(msgId, { content: publicUrl, fileName: file.name });
    } catch (e) { console.error(e); }
    setUploadingHubMedia(null);
  };

  const handleDeleteHub = async (hubId: string) => {
    await supabase.from('whatsapp_group_hubs').delete().eq('id', hubId);
    fetchGroupHubs();
  };

  // ─── Broadcast Monitor ───
  const fetchBroadcastData = useCallback(async () => {
    setLoadingBroadcasts(true);
    setBroadcastData(null);
    try {
      const data = await edgeFn('broadcast-discover');
      if (data?.messages) {
        setBroadcastData(data);
      }
    } catch { setBroadcastData(null); }
    setLoadingBroadcasts(false);
  }, [edgeFn]);

  useEffect(() => {
    if (subView === 'broadcasts' && sessionStatus === 'connected') fetchBroadcastData();
  }, [subView, sessionStatus]);

  // ─── Formatters ───
  const formatTime = (ts: number) => new Date(ts * 1000).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

  const formatDate = (ts: number) => {
    const d = new Date(ts * 1000);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) return 'Hoje';
    const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return 'Ontem';
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  };

  const isMaskedName = (name: string) => !name || name.includes('∙') || /^\+\d{2}[∙.·•]+/.test(name);

  const formatPhone = (digits: string) => {
    if (digits.startsWith('55') && digits.length >= 12) {
      const ddd = digits.slice(2, 4);
      const num = digits.slice(4);
      return `+55 (${ddd}) ${num.slice(0, -4)}-${num.slice(-4)}`;
    }
    return `+${digits}`;
  };

  const formatChatName = (chat: Chat) => {
    if (chat.name && !isMaskedName(chat.name)) return chat.name;
    const raw = chat.id.replace('@s.whatsapp.net', '').replace('@c.us', '');
    if (chat.id.includes('@g.us')) return chat.name || 'Grupo';
    if (/^\d{10,}$/.test(raw)) return formatPhone(raw);
    return chat.name || raw;
  };

  // ─── Message status icon ───
  const AckIcon = ({ ack, fromMe }: { ack?: number; fromMe: boolean }) => {
    if (!fromMe) return null;
    if (ack === 3 || ack === 4) return <CheckCheck size={14} className="text-blue-500 inline-block ml-1" />;
    if (ack === 2) return <CheckCheck size={14} className="text-neutral-400 inline-block ml-1" />;
    if (ack === 1) return <Check size={14} className="text-neutral-400 inline-block ml-1" />;
    return <Clock size={12} className="text-neutral-400 inline-block ml-1" />;
  };

  // ─── Chat filtering ───
  const isGroup = (chat: Chat) => chat.id.includes('@g.us');
  const groupChats = chats.filter(c => isGroup(c));

  const isBroadcast = (chat: Chat) => chat.id.includes('@broadcast') && chat.id !== 'status@broadcast';

  const filteredChats = chats.filter(c => {
    const name = formatChatName(c).toLowerCase();
    const term = searchTerm.toLowerCase();
    const matchSearch = !term || name.includes(term) || c.id.includes(searchTerm);
    if (subView === 'main') return matchSearch && !isGroup(c) && !isBroadcast(c);
    if (chatTab === 'contacts') return matchSearch && !isGroup(c) && !isBroadcast(c);
    if (chatTab === 'groups') return matchSearch && isGroup(c);
    return matchSearch && !isBroadcast(c);
  });

  // ─── SETTINGS VIEW (independent of connection) ───
  if (subView === 'settings') {
    const Toggle = ({ value, onChange, label, desc }: { value: boolean; onChange: () => void; label: string; desc: string }) => (
      <div className="flex items-center justify-between py-3">
        <div><p className="font-bold text-navy-950 dark:text-white text-sm">{label}</p><p className="text-xs text-neutral-500">{desc}</p></div>
        <button onClick={onChange} className={`w-12 h-6 rounded-full transition-all relative flex-shrink-0 ${value ? 'bg-emerald-600' : 'bg-neutral-300 dark:bg-navy-700'}`}>
          <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all shadow ${value ? 'left-7' : 'left-1'}`} />
        </button>
      </div>
    );

    if (!configLoaded) {
      return (
        <div className="max-w-2xl mx-auto py-20">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="animate-spin text-emerald-600" size={32} />
            <p className="text-sm text-neutral-400 font-bold">Carregando configurações...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="max-w-2xl mx-auto py-10">
        <button onClick={() => onNavigate?.('whatsapp')} className="md:hidden flex items-center gap-2 text-neutral-500 hover:text-navy-950 dark:hover:text-white mb-8 font-bold text-sm transition-colors"><ArrowLeft size={18} /> Voltar</button>
        <h2 className="text-3xl font-black tracking-tighter mb-2 text-navy-950 dark:text-white">Configurações do WhatsApp</h2>
        <p className="text-neutral-500 dark:text-slate-400 text-sm mb-10">Configure lembretes, notificações e gerencie a conexão.</p>

        {/* Connection */}
        {sessionStatus === 'connected' ? (
          <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-2xl p-6 mb-8 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center"><Phone className="text-white" size={20} /></div>
              <div><p className="font-black text-navy-950 dark:text-white">Conectado</p><p className="text-sm text-emerald-700 dark:text-emerald-400 font-mono">{phoneNumber || '—'}</p></div>
            </div>
            <button onClick={handleDisconnect} disabled={actionLoading} className="px-5 py-2.5 bg-red-100 dark:bg-red-900/30 text-red-600 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-red-200 transition-all flex items-center gap-2">
              {actionLoading ? <Loader2 className="animate-spin" size={14} /> : <LogOut size={14} />} Desconectar
            </button>
          </div>
        ) : (
          <div className="bg-neutral-50 dark:bg-navy-800 border border-neutral-200 dark:border-navy-700 rounded-2xl p-6 mb-8 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-neutral-300 dark:bg-navy-600 rounded-full flex items-center justify-center"><Phone className="text-neutral-500" size={20} /></div>
              <div><p className="font-black text-navy-950 dark:text-white">Desconectado</p><p className="text-sm text-neutral-400">Conecte na aba Conversas</p></div>
            </div>
            <button onClick={() => onNavigate?.('whatsapp')} className="px-5 py-2.5 bg-emerald-600 text-white rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-emerald-700 transition-all flex items-center gap-2">
              <MessageSquare size={14} /> Ir para Conversas
            </button>
          </div>
        )}

        {/* Master toggle */}
        <div className="bg-white dark:bg-navy-900 border border-neutral-200 dark:border-navy-800 rounded-2xl p-6 mb-6">
          <Toggle value={reminderConfig.active} onChange={() => setReminderConfig(c => ({ ...c, active: !c.active }))} label="Ativar notificações WhatsApp" desc="Habilita todos os lembretes e notificações via WhatsApp" />
        </div>

        {/* Scheduled reminders */}
        <div className="bg-white dark:bg-navy-900 border border-neutral-200 dark:border-navy-800 rounded-2xl p-6 mb-6">
          <h3 className="text-lg font-black text-navy-950 dark:text-white mb-1 flex items-center gap-2"><Clock size={18} className="text-emerald-600" /> Lembretes de Prazo</h3>
          <p className="text-xs text-neutral-500 mb-4">Lembretes automáticos baseados na data de vencimento das tarefas.</p>

          <div className="space-y-1 mb-6">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Horário do lembrete (Brasília)</label>
            <input
              type="time"
              value={`${String(reminderConfig.reminder_hour).padStart(2, '0')}:${String(reminderConfig.reminder_minute).padStart(2, '0')}`}
              onChange={e => {
                const [h, m] = e.target.value.split(':').map(Number)
                setReminderConfig(c => ({ ...c, reminder_hour: h, reminder_minute: m }))
              }}
              className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-700 rounded-xl px-4 py-3 font-bold text-sm outline-none dark:text-white"
            />
          </div>

          <div className="border-t border-neutral-100 dark:border-navy-800">
            <Toggle value={reminderConfig.remind_on_due_day} onChange={() => setReminderConfig(c => ({ ...c, remind_on_due_day: !c.remind_on_due_day }))} label="No dia do vencimento" desc="Lembrete no dia que a tarefa vence" />
            <Toggle value={reminderConfig.remind_day_before} onChange={() => setReminderConfig(c => ({ ...c, remind_day_before: !c.remind_day_before }))} label="Um dia antes" desc="Lembrete na véspera do vencimento" />
            <Toggle value={reminderConfig.remind_week_before} onChange={() => setReminderConfig(c => ({ ...c, remind_week_before: !c.remind_week_before }))} label="Uma semana antes" desc="Lembrete 7 dias antes do vencimento" />
          </div>
        </div>

        {/* Event-based notifications */}
        <div className="bg-white dark:bg-navy-900 border border-neutral-200 dark:border-navy-800 rounded-2xl p-6 mb-6">
          <h3 className="text-lg font-black text-navy-950 dark:text-white mb-1 flex items-center gap-2"><Bell size={18} className="text-emerald-600" /> Notificações em Tempo Real</h3>
          <p className="text-xs text-neutral-500 mb-4">Notificações disparadas imediatamente quando eventos acontecem.</p>

          {/* Header */}
          <div className="flex items-center border-b border-neutral-200 dark:border-navy-700 pb-2 mb-2">
            <div className="flex-1"><p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Evento</p></div>
            <div className="w-24 text-center"><p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Responsável</p></div>
            <div className="w-24 text-center"><p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Stakeholder</p></div>
          </div>

          {/* Rows */}
          {([
            { key: 'notify_task_created', label: 'Tarefa criada', desc: 'Ao criar tarefa', resp: true, stake: true },
            { key: 'notify_task_assigned', label: 'Tarefa reatribuída', desc: 'Ao transferir tarefa', resp: true, stake: true },
            { key: 'notify_status_changed', label: 'Status alterado', desc: 'Quando status muda', resp: true, stake: true },
            { key: 'notify_task_completed', label: 'Tarefa concluída', desc: 'Quando tarefa é concluída', resp: true, stake: true },
            { key: 'notify_comment_added', label: 'Comentário adicionado', desc: 'Quando alguém comenta', resp: true, stake: true },
          ] as const).map(({ key, label, desc, resp, stake }) => {
            const respKey = `${key}_resp` as keyof ReminderConfig;
            const stakeKey = `${key}_stake` as keyof ReminderConfig;
            const singleKey = key as keyof ReminderConfig;
            const respVal = resp && stake ? (reminderConfig as any)[respKey] ?? (reminderConfig as any)[singleKey] ?? true : (reminderConfig as any)[singleKey] ?? true;
            const stakeVal = resp && stake ? (reminderConfig as any)[stakeKey] ?? (reminderConfig as any)[singleKey] ?? true : (reminderConfig as any)[singleKey] ?? true;

            return (
              <div key={key} className="flex items-center py-2.5 border-b border-neutral-50 dark:border-navy-800/50">
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-navy-950 dark:text-white text-sm">{label}</p>
                  <p className="text-[10px] text-neutral-400">{desc}</p>
                </div>
                <div className="w-24 flex justify-center">
                  {resp ? (
                    <button onClick={() => setReminderConfig(c => ({ ...c, [resp && stake ? respKey : singleKey]: !respVal } as any))} className={`w-10 h-5 rounded-full transition-all relative ${respVal ? 'bg-emerald-600' : 'bg-neutral-300 dark:bg-navy-700'}`}>
                      <div className={`w-3.5 h-3.5 bg-white rounded-full absolute top-[3px] transition-all shadow ${respVal ? 'left-[22px]' : 'left-[3px]'}`} />
                    </button>
                  ) : <span className="text-neutral-300 text-xs">—</span>}
                </div>
                <div className="w-24 flex justify-center">
                  {stake ? (
                    <button onClick={() => setReminderConfig(c => ({ ...c, [resp && stake ? stakeKey : singleKey]: !stakeVal } as any))} className={`w-10 h-5 rounded-full transition-all relative ${stakeVal ? 'bg-emerald-600' : 'bg-neutral-300 dark:bg-navy-700'}`}>
                      <div className={`w-3.5 h-3.5 bg-white rounded-full absolute top-[3px] transition-all shadow ${stakeVal ? 'left-[22px]' : 'left-[3px]'}`} />
                    </button>
                  ) : <span className="text-neutral-300 text-xs">—</span>}
                </div>
              </div>
            );
          })}
        </div>

        {/* Save */}
        <div className="bg-white dark:bg-navy-900 border border-neutral-200 dark:border-navy-800 rounded-2xl p-6">
          <button onClick={handleSaveConfig} disabled={savingConfig} className="w-full py-4 bg-navy-950 dark:bg-emerald-600 text-white rounded-xl font-black text-xs uppercase tracking-widest shadow-lg flex items-center justify-center gap-2 hover:opacity-90 transition-all">
            {savingConfig ? <Loader2 className="animate-spin" size={16} /> : configSaved ? <><CheckCircle2 size={16} /> Salvo!</> : <><Save size={16} /> Salvar Configurações</>}
          </button>
        </div>
      </div>
    );
  }

  // ─── FLOWS VIEW ───
  if (subView === 'flows') {
    const allTags = Array.from(new Set((crmContacts || []).flatMap(c => c.tags || [])));

    const flowUpdateMessage = (id: string, updates: Partial<BirthdayMessage>) => {
      setFlowMessages(prev => prev.map(m => m.id === id ? { ...m, ...updates } : m));
    };
    const flowAddMessage = () => {
      setFlowMessages(prev => [...prev, { id: String(Date.now()), type: 'text' as const, content: '', intervalMin: 10, intervalMax: 20, showPresence: false, presenceType: 'typing' as const }]);
    };
    const flowRemoveMessage = (id: string) => {
      if (flowMessages.length <= 1) return;
      setFlowMessages(prev => prev.filter(m => m.id !== id));
    };
    const flowHandleMediaUpload = async (msgId: string, file: File) => {
      setUploadingMedia(msgId);
      try {
        const ext = file.name.split('.').pop();
        const path = `flows/${container?.id}/${Date.now()}.${ext}`;
        const { error } = await supabase.storage.from('whatsapp-media').upload(path, file);
        if (error) { setUploadingMedia(null); return; }
        const { data: { publicUrl } } = supabase.storage.from('whatsapp-media').getPublicUrl(path);
        flowUpdateMessage(msgId, { content: publicUrl, fileName: file.name });
      } catch (e) { console.error(e); }
      setUploadingMedia(null);
    };
    const acceptMap: Record<string, string> = { image: 'image/*', audio: 'audio/*', video: 'video/*' };
    const startEditFlow = (flow: WhatsAppFlow) => {
      setEditingFlow(flow);
      setFlowName(flow.name);
      setFlowMessages(flow.messages);
      setFlowTags(flow.target_tags);
    };

    return (
      <div className="max-w-3xl mx-auto py-10">
        <button onClick={() => onNavigate?.('whatsapp')} className="md:hidden flex items-center gap-2 text-neutral-500 hover:text-navy-950 dark:hover:text-white mb-8 font-bold text-sm transition-colors"><ArrowLeft size={18} /> Voltar</button>
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center"><Send className="text-blue-600" size={22} /></div>
          <div>
            <h2 className="text-3xl font-black tracking-tighter text-navy-950 dark:text-white">Disparos</h2>
            <p className="text-neutral-500 dark:text-slate-400 text-sm">Monte fluxos de mensagens e envie para contatos com determinadas tags.</p>
          </div>
        </div>

        {/* Flow editor */}
        <div className="bg-white dark:bg-navy-900 border border-neutral-200 dark:border-navy-800 rounded-2xl p-6 mb-6">
          <h3 className="text-lg font-black text-navy-950 dark:text-white mb-4 flex items-center gap-2">
            <MessageSquare size={18} className="text-blue-600" />
            {editingFlow ? 'Editar Fluxo' : 'Novo Fluxo'}
          </h3>

          <div className="mb-4">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Nome do fluxo</label>
            <input type="text" value={flowName} onChange={e => setFlowName(e.target.value)} placeholder="Ex: Boas-vindas, Campanha..."
              className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-700 rounded-xl px-4 py-3 font-bold text-sm outline-none dark:text-white mt-1" />
          </div>

          {/* Tag targeting */}
          <div className="mb-5">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Tags alvo (deixe vazio para todos)</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {allTags.map(tag => (
                <button key={tag} onClick={() => setFlowTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag])}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${flowTags.includes(tag) ? 'bg-blue-500 text-white' : 'bg-neutral-100 dark:bg-navy-800 text-neutral-500 hover:bg-blue-100 dark:hover:bg-blue-900/30'}`}>
                  <Tag size={10} className="inline mr-1" />{tag}
                </button>
              ))}
              {allTags.length === 0 && <p className="text-xs text-neutral-400 italic">Nenhuma tag encontrada nos contatos.</p>}
            </div>
          </div>

          {/* Messages - reuse same pattern as birthday */}
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Mensagens do fluxo</label>
          <p className="text-xs text-neutral-500 mb-4">Use <code className="bg-neutral-100 dark:bg-navy-800 px-1.5 py-0.5 rounded text-blue-600 font-bold text-[11px]">{'{nome}'}</code> para o nome do contato.</p>

          <div className="space-y-3">
            {flowMessages.map((msg, i) => (
              <React.Fragment key={msg.id}>
                {i > 0 && (
                  <div className="flex flex-col items-center gap-1 py-1">
                    <div className="h-3 border-l-2 border-dashed border-neutral-300 dark:border-navy-600" />
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-neutral-100 dark:bg-navy-800 rounded-xl">
                      <Clock size={11} className="text-neutral-400" />
                      <span className="text-[10px] font-bold text-neutral-400">aguardar</span>
                      <input type="number" min={1} max={600} value={msg.intervalMin ?? 10} onChange={e => flowUpdateMessage(msg.id, { intervalMin: Number(e.target.value) })}
                        className="w-12 bg-white dark:bg-navy-950 border border-neutral-200 dark:border-navy-700 rounded-md px-1 py-0.5 text-[10px] font-bold text-center outline-none dark:text-white" />
                      <span className="text-[10px] text-neutral-400">a</span>
                      <input type="number" min={1} max={600} value={msg.intervalMax ?? 20} onChange={e => flowUpdateMessage(msg.id, { intervalMax: Number(e.target.value) })}
                        className="w-12 bg-white dark:bg-navy-950 border border-neutral-200 dark:border-navy-700 rounded-md px-1 py-0.5 text-[10px] font-bold text-center outline-none dark:text-white" />
                      <span className="text-[10px] text-neutral-500 font-bold">seg</span>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1">
                      <button onClick={() => flowUpdateMessage(msg.id, { showPresence: !msg.showPresence })}
                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all ${msg.showPresence ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600' : 'bg-neutral-50 dark:bg-navy-900 text-neutral-400 hover:text-neutral-600'}`}>
                        {msg.showPresence ? '✓' : '+'} {msg.type === 'audio' ? 'Gravando áudio...' : 'Digitando...'}
                      </button>
                      {msg.showPresence && msg.type !== 'audio' && (
                        <select value={msg.presenceType || 'typing'} onChange={e => flowUpdateMessage(msg.id, { presenceType: e.target.value as 'typing' | 'recording' })}
                          className="bg-neutral-50 dark:bg-navy-950 border border-neutral-200 dark:border-navy-700 rounded-md px-1.5 py-0.5 text-[10px] font-bold outline-none dark:text-white">
                          <option value="typing">✏️ Digitando</option>
                          <option value="recording">🎙️ Gravando</option>
                        </select>
                      )}
                    </div>
                    <div className="h-3 border-l-2 border-dashed border-neutral-300 dark:border-navy-600" />
                  </div>
                )}

                <div className="border border-neutral-200 dark:border-navy-700 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Mensagem {i + 1}</span>
                      <select value={msg.type} onChange={e => {
                        const newType = e.target.value as any;
                        const updates: Partial<BirthdayMessage> = { type: newType, content: '', fileName: undefined };
                        if (newType === 'audio') updates.presenceType = 'recording';
                        else if (msg.presenceType === 'recording') updates.presenceType = 'typing';
                        flowUpdateMessage(msg.id, updates);
                      }}
                        className="bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-700 rounded-lg px-2.5 py-1.5 text-xs font-bold outline-none dark:text-white">
                        <option value="text">📝 Texto</option>
                        <option value="image">🖼️ Imagem</option>
                        <option value="audio">🎙️ Áudio (voz)</option>
                        <option value="video">🎬 Vídeo</option>
                      </select>
                    </div>
                    {flowMessages.length > 1 && (
                      <button onClick={() => flowRemoveMessage(msg.id)} className="p-1.5 text-neutral-400 hover:text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"><Trash2 size={14} /></button>
                    )}
                  </div>
                  {msg.type === 'text' ? (
                    <textarea value={msg.content} onChange={e => flowUpdateMessage(msg.id, { content: e.target.value })} rows={3} placeholder="Digite a mensagem..."
                      className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-700 rounded-xl px-4 py-3 text-sm outline-none dark:text-white resize-none" />
                  ) : (
                    <div>
                      {uploadingMedia === msg.id ? (
                        <div className="flex items-center gap-2 p-4 bg-slate-50 dark:bg-navy-950 rounded-xl"><Loader2 className="animate-spin text-blue-500" size={18} /><span className="text-sm text-neutral-400">Enviando...</span></div>
                      ) : msg.content ? (
                        <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-navy-950 rounded-xl">
                          {msg.type === 'image' && <img src={msg.content} alt="" className="h-16 w-16 object-cover rounded-lg" />}
                          {msg.type === 'audio' && <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center flex-shrink-0"><span className="text-lg">🎙️</span></div>}
                          {msg.type === 'video' && <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center flex-shrink-0"><span className="text-lg">🎬</span></div>}
                          <div className="flex-1 min-w-0">
                            <span className="text-sm text-navy-950 dark:text-white font-bold truncate block">{msg.fileName || 'Arquivo'}</span>
                            {msg.type === 'audio' && <span className="text-[10px] text-emerald-500 font-bold">Será enviado como nota de voz</span>}
                          </div>
                          <button onClick={() => flowUpdateMessage(msg.id, { content: '', fileName: undefined })} className="p-1.5 text-neutral-400 hover:text-red-500 transition-all"><X size={14} /></button>
                        </div>
                      ) : (
                        <label className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-navy-950 rounded-xl border-2 border-dashed border-neutral-200 dark:border-navy-700 cursor-pointer hover:border-blue-400 transition-all">
                          <Upload size={20} className="text-neutral-400" />
                          <span className="text-sm text-neutral-500 font-bold">Clique para enviar {msg.type === 'image' ? 'uma imagem' : msg.type === 'audio' ? 'um áudio' : 'um vídeo'}</span>
                          <input type="file" hidden accept={acceptMap[msg.type]} onChange={e => { const f = e.target.files?.[0]; if (f) flowHandleMediaUpload(msg.id, f); e.target.value = ''; }} />
                        </label>
                      )}
                    </div>
                  )}
                </div>
              </React.Fragment>
            ))}
          </div>

          <div className="mt-4 flex items-center justify-between">
            <button onClick={flowAddMessage} className="flex items-center gap-2 px-4 py-2.5 border-2 border-dashed border-neutral-300 dark:border-navy-600 rounded-xl text-sm font-bold text-neutral-500 hover:border-blue-400 hover:text-blue-500 transition-all">
              <Plus size={16} /> Adicionar mensagem
            </button>
            <div className="flex items-center gap-2">
              {editingFlow && (
                <button onClick={() => { setEditingFlow(null); setFlowName(''); setFlowMessages([{ id: '1', type: 'text', content: '' }]); setFlowTags([]); }}
                  className="px-4 py-2.5 rounded-xl text-sm font-bold text-neutral-500 hover:text-neutral-700 transition-all">Cancelar</button>
              )}
              <button onClick={handleSaveFlow} disabled={savingFlow || !flowName.trim()}
                className="flex items-center gap-2 px-5 py-2.5 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white rounded-xl text-sm font-black transition-all">
                {savingFlow ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                {editingFlow ? 'Salvar' : 'Criar Fluxo'}
              </button>
            </div>
          </div>
        </div>

        {/* Saved flows list */}
        <div className="bg-white dark:bg-navy-900 border border-neutral-200 dark:border-navy-800 rounded-2xl p-6">
          <h3 className="text-lg font-black text-navy-950 dark:text-white mb-4 flex items-center gap-2"><Send size={18} className="text-blue-600" /> Fluxos Salvos</h3>
          {loadingFlows ? (
            <div className="text-center py-10"><Loader2 className="animate-spin text-blue-500 mx-auto" size={24} /></div>
          ) : flows.length === 0 ? (
            <div className="text-center py-10"><Send size={32} className="text-neutral-300 dark:text-navy-700 mx-auto mb-3" /><p className="text-neutral-400 text-sm">Nenhum fluxo criado ainda.</p></div>
          ) : (
            <div className="space-y-3">
              {flows.map(flow => {
                const targetCount = flow.target_tags.length === 0
                  ? (crmContacts || []).filter(c => (c.phones || [])[0] && !c.deleted_at).length
                  : (crmContacts || []).filter(c => (c.phones || [])[0] && !c.deleted_at && flow.target_tags.some(tag => (c.tags || []).includes(tag))).length;
                return (
                  <div key={flow.id} className="border border-neutral-200 dark:border-navy-700 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h4 className="font-black text-navy-950 dark:text-white">{flow.name}</h4>
                        <p className="text-xs text-neutral-400">{flow.messages.length} mensage{flow.messages.length > 1 ? 'ns' : 'm'} · {targetCount} contato{targetCount !== 1 ? 's' : ''} alvo</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => startEditFlow(flow)} className="p-2 text-neutral-400 hover:text-blue-500 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all"><Edit2 size={14} /></button>
                        <button onClick={() => handleDeleteFlow(flow.id)} className="p-2 text-neutral-400 hover:text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"><Trash2 size={14} /></button>
                        <button onClick={() => handleSendFlow(flow)} disabled={sendingFlowId === flow.id || targetCount === 0}
                          className="flex items-center gap-1.5 px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white rounded-lg text-xs font-black transition-all">
                          {sendingFlowId === flow.id ? <Loader2 className="animate-spin" size={12} /> : <Send size={12} />}
                          Disparar
                        </button>
                      </div>
                    </div>
                    {flow.target_tags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {flow.target_tags.map(tag => (
                          <span key={tag} className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 rounded-full text-[10px] font-bold"><Tag size={8} className="inline mr-0.5" />{tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─── BIRTHDAY VIEW (independent of connection) ───
  if (subView === 'birthdays') {
    if (!configLoaded) {
      return (
        <div className="max-w-3xl mx-auto py-20">
          <div className="flex flex-col items-center gap-3"><Loader2 className="animate-spin text-pink-600" size={32} /><p className="text-sm text-neutral-400 font-bold">Carregando...</p></div>
        </div>
      );
    }

    const today = new Date();
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const upcoming = (crmContacts || [])
      .filter(c => c.birthdate && !c.deleted_at)
      .map(c => {
        const [, m, d] = c.birthdate!.split('-').map(Number);
        const thisYear = new Date(today.getFullYear(), m - 1, d);
        if (thisYear < todayStart) thisYear.setFullYear(thisYear.getFullYear() + 1);
        const daysUntil = Math.round((thisYear.getTime() - todayStart.getTime()) / 86400000);
        return { ...c, daysUntil, nextBirthday: thisYear };
      })
      .sort((a, b) => a.daysUntil - b.daysUntil)
      .slice(0, 30);

    const addMessage = () => {
      setBirthdayConfig(prev => ({ ...prev, messages: [...prev.messages, { id: String(Date.now()), type: 'text' as const, content: '', intervalMin: prev.msgIntervalMin, intervalMax: prev.msgIntervalMax, showPresence: false, presenceType: 'typing' as const }] }));
    };
    const removeMessage = (id: string) => {
      if (birthdayConfig.messages.length <= 1) return;
      setBirthdayConfig(prev => ({ ...prev, messages: prev.messages.filter(m => m.id !== id) }));
    };
    const updateMessage = (id: string, updates: Partial<BirthdayMessage>) => {
      setBirthdayConfig(prev => ({ ...prev, messages: prev.messages.map(m => m.id === id ? { ...m, ...updates } : m) }));
    };
    const handleMediaUpload = async (msgId: string, file: File) => {
      setUploadingMedia(msgId);
      try {
        const ext = file.name.split('.').pop();
        const path = `birthday/${container?.id}/${Date.now()}.${ext}`;
        const { error } = await supabase.storage.from('whatsapp-media').upload(path, file);
        if (error) { console.error('Upload error:', error); setUploadingMedia(null); return; }
        const { data: { publicUrl } } = supabase.storage.from('whatsapp-media').getPublicUrl(path);
        updateMessage(msgId, { content: publicUrl, fileName: file.name });
      } catch (e) { console.error(e); }
      setUploadingMedia(null);
    };
    const acceptMap: Record<string, string> = { image: 'image/*', audio: 'audio/*', video: 'video/*' };

    const Toggle = ({ value, onChange, label, desc }: { value: boolean; onChange: () => void; label: string; desc: string }) => (
      <div className="flex items-center justify-between py-3">
        <div><p className="font-bold text-navy-950 dark:text-white text-sm">{label}</p><p className="text-xs text-neutral-500">{desc}</p></div>
        <button onClick={onChange} className={`w-12 h-6 rounded-full transition-all relative flex-shrink-0 ${value ? 'bg-pink-500' : 'bg-neutral-300 dark:bg-navy-700'}`}>
          <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all shadow ${value ? 'left-7' : 'left-1'}`} />
        </button>
      </div>
    );

    return (
      <div className="max-w-3xl mx-auto py-10">
        <button onClick={() => onNavigate?.('whatsapp')} className="md:hidden flex items-center gap-2 text-neutral-500 hover:text-navy-950 dark:hover:text-white mb-8 font-bold text-sm transition-colors"><ArrowLeft size={18} /> Voltar</button>
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-pink-100 dark:bg-pink-900/30 rounded-xl flex items-center justify-center"><Cake className="text-pink-600" size={22} /></div>
          <div>
            <h2 className="text-3xl font-black tracking-tighter text-navy-950 dark:text-white">Aniversários</h2>
            <p className="text-neutral-500 dark:text-slate-400 text-sm">Envie mensagens personalizadas de aniversário via WhatsApp.</p>
          </div>
        </div>

        {/* Active toggle */}
        <div className="bg-white dark:bg-navy-900 border border-neutral-200 dark:border-navy-800 rounded-2xl p-6 mb-6">
          <Toggle value={birthdayConfig.active} onChange={() => setBirthdayConfig(c => ({ ...c, active: !c.active }))} label="Ativar envio de aniversários" desc="Envia mensagens automaticamente no dia do aniversário dos contatos" />
        </div>

        {/* Start hour + dispatch settings */}
        <div className="bg-white dark:bg-navy-900 border border-neutral-200 dark:border-navy-800 rounded-2xl p-6 mb-6">
          <h3 className="text-lg font-black text-navy-950 dark:text-white mb-1 flex items-center gap-2"><Clock size={18} className="text-pink-600" /> Horário e Limites</h3>
          <p className="text-xs text-neutral-500 mb-4">Quando e como os envios serão feitos.</p>

          <div className="space-y-4">
            <div>
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Horário de início do disparo (Brasília)</label>
              <input type="time" value={`${String(birthdayConfig.startHour).padStart(2, '0')}:${String(birthdayConfig.startMinute).padStart(2, '0')}`} onChange={e => { const [h, m] = e.target.value.split(':').map(Number); if (!isNaN(h) && !isNaN(m)) setBirthdayConfig(c => ({ ...c, startHour: h, startMinute: m })); }}
                className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-700 rounded-xl px-4 py-3 font-bold text-sm outline-none dark:text-white mt-1" />
            </div>

            <div className="border-t border-neutral-100 dark:border-navy-800 pt-4">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Intervalo entre contatos (quando há vários no mesmo dia)</label>
              <div className="flex items-center gap-2 mt-1.5">
                <input type="number" min={1} max={30} value={birthdayConfig.recipientIntervalMin} onChange={e => setBirthdayConfig(c => ({ ...c, recipientIntervalMin: Number(e.target.value) }))}
                  className="w-20 bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-700 rounded-xl px-3 py-2.5 font-bold text-sm text-center outline-none dark:text-white" />
                <span className="text-sm text-neutral-400">a</span>
                <input type="number" min={1} max={60} value={birthdayConfig.recipientIntervalMax} onChange={e => setBirthdayConfig(c => ({ ...c, recipientIntervalMax: Number(e.target.value) }))}
                  className="w-20 bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-700 rounded-xl px-3 py-2.5 font-bold text-sm text-center outline-none dark:text-white" />
                <span className="text-sm text-neutral-500 font-bold">minutos</span>
                <span className="text-[10px] text-neutral-400 ml-1">(aleatório)</span>
              </div>
            </div>

            <div className="border-t border-neutral-100 dark:border-navy-800 pt-4">
              <div className="flex items-center gap-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Limite diário de envios</label>
                <div className="relative group">
                  <Info size={14} className="text-amber-500 cursor-help" />
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-navy-900 text-white text-xs rounded-xl shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                    Altos números de disparos diários podem levar ao banimento do número pelo WhatsApp. Recomendamos manter abaixo de 50 envios/dia.
                  </div>
                </div>
              </div>
              <input type="number" min={1} max={200} value={birthdayConfig.dailyLimit} onChange={e => setBirthdayConfig(c => ({ ...c, dailyLimit: Number(e.target.value) }))}
                className="w-28 bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-700 rounded-xl px-3 py-2.5 font-bold text-sm text-center outline-none dark:text-white mt-1.5" />
              {birthdayConfig.dailyLimit > 50 && (
                <p className="text-amber-500 text-xs font-bold mt-1.5 flex items-center gap-1"><Info size={12} /> Alto volume — risco de banimento</p>
              )}
            </div>
          </div>
        </div>

        {/* Message builder */}
        <div className="bg-white dark:bg-navy-900 border border-neutral-200 dark:border-navy-800 rounded-2xl p-6 mb-6">
          <h3 className="text-lg font-black text-navy-950 dark:text-white mb-1 flex items-center gap-2"><MessageSquare size={18} className="text-pink-600" /> Mensagens</h3>
          <p className="text-xs text-neutral-500 mb-5">Use <code className="bg-neutral-100 dark:bg-navy-800 px-1.5 py-0.5 rounded text-pink-600 font-bold text-[11px]">{'{nome}'}</code> para o nome do contato. Adicione quantas mensagens quiser.</p>

          <div className="space-y-3">
            {birthdayConfig.messages.map((msg, i) => (
              <React.Fragment key={msg.id}>
                {/* Per-message interval + presence (not for first message) */}
                {i > 0 && (
                  <div className="flex flex-col items-center gap-1 py-1">
                    <div className="h-3 border-l-2 border-dashed border-neutral-300 dark:border-navy-600" />
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-neutral-100 dark:bg-navy-800 rounded-xl">
                      <Clock size={11} className="text-neutral-400" />
                      <span className="text-[10px] font-bold text-neutral-400">aguardar</span>
                      <input type="number" min={1} max={600} value={msg.intervalMin ?? birthdayConfig.msgIntervalMin} onChange={e => updateMessage(msg.id, { intervalMin: Number(e.target.value) })}
                        className="w-12 bg-white dark:bg-navy-950 border border-neutral-200 dark:border-navy-700 rounded-md px-1 py-0.5 text-[10px] font-bold text-center outline-none dark:text-white" />
                      <span className="text-[10px] text-neutral-400">a</span>
                      <input type="number" min={1} max={600} value={msg.intervalMax ?? birthdayConfig.msgIntervalMax} onChange={e => updateMessage(msg.id, { intervalMax: Number(e.target.value) })}
                        className="w-12 bg-white dark:bg-navy-950 border border-neutral-200 dark:border-navy-700 rounded-md px-1 py-0.5 text-[10px] font-bold text-center outline-none dark:text-white" />
                      <span className="text-[10px] text-neutral-500 font-bold">seg</span>
                    </div>
                    {/* Presence simulation toggle */}
                    <div className="flex items-center gap-2 px-3 py-1">
                      <button onClick={() => updateMessage(msg.id, { showPresence: !msg.showPresence })}
                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all ${msg.showPresence ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600' : 'bg-neutral-50 dark:bg-navy-900 text-neutral-400 hover:text-neutral-600'}`}>
                        {msg.showPresence ? '✓' : '+'} {msg.type === 'audio' ? 'Gravando áudio...' : 'Digitando...'}
                      </button>
                      {msg.showPresence && msg.type !== 'audio' && (
                        <select value={msg.presenceType || 'typing'} onChange={e => updateMessage(msg.id, { presenceType: e.target.value as 'typing' | 'recording' })}
                          className="bg-neutral-50 dark:bg-navy-950 border border-neutral-200 dark:border-navy-700 rounded-md px-1.5 py-0.5 text-[10px] font-bold outline-none dark:text-white">
                          <option value="typing">✏️ Digitando</option>
                          <option value="recording">🎙️ Gravando</option>
                        </select>
                      )}
                    </div>
                    <div className="h-3 border-l-2 border-dashed border-neutral-300 dark:border-navy-600" />
                  </div>
                )}

                <div className="border border-neutral-200 dark:border-navy-700 rounded-xl p-4 relative">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Mensagem {i + 1}</span>
                      <select value={msg.type} onChange={e => {
                        const newType = e.target.value as any;
                        const updates: Partial<BirthdayMessage> = { type: newType, content: '', fileName: undefined };
                        if (newType === 'audio') updates.presenceType = 'recording';
                        else if (msg.presenceType === 'recording') updates.presenceType = 'typing';
                        updateMessage(msg.id, updates);
                      }}
                        className="bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-700 rounded-lg px-2.5 py-1.5 text-xs font-bold outline-none dark:text-white">
                        <option value="text">📝 Texto</option>
                        <option value="image">🖼️ Imagem</option>
                        <option value="audio">🎙️ Áudio (voz)</option>
                        <option value="video">🎬 Vídeo</option>
                      </select>
                    </div>
                    {birthdayConfig.messages.length > 1 && (
                      <button onClick={() => removeMessage(msg.id)} className="p-1.5 text-neutral-400 hover:text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"><Trash2 size={14} /></button>
                    )}
                  </div>

                  {msg.type === 'text' ? (
                    <textarea value={msg.content} onChange={e => updateMessage(msg.id, { content: e.target.value })} rows={3} placeholder="Digite a mensagem..."
                      className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-700 rounded-xl px-4 py-3 text-sm outline-none dark:text-white resize-none" />
                  ) : (
                    <div>
                      {uploadingMedia === msg.id ? (
                        <div className="flex items-center gap-2 p-4 bg-slate-50 dark:bg-navy-950 rounded-xl"><Loader2 className="animate-spin text-pink-500" size={18} /><span className="text-sm text-neutral-400">Enviando...</span></div>
                      ) : msg.content ? (
                        <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-navy-950 rounded-xl">
                          {msg.type === 'image' && <img src={msg.content} alt="" className="h-16 w-16 object-cover rounded-lg" />}
                          {msg.type === 'audio' && <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center flex-shrink-0"><span className="text-lg">🎙️</span></div>}
                          {msg.type === 'video' && <div className="w-10 h-10 bg-pink-100 dark:bg-pink-900/30 rounded-lg flex items-center justify-center flex-shrink-0"><span className="text-lg">🎬</span></div>}
                          <div className="flex-1 min-w-0">
                            <span className="text-sm text-navy-950 dark:text-white font-bold truncate block">{msg.fileName || 'Arquivo'}</span>
                            {msg.type === 'audio' && <span className="text-[10px] text-emerald-500 font-bold">Será enviado como nota de voz</span>}
                          </div>
                          <button onClick={() => updateMessage(msg.id, { content: '', fileName: undefined })} className="p-1.5 text-neutral-400 hover:text-red-500 transition-all"><X size={14} /></button>
                        </div>
                      ) : (
                        <label className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-navy-950 rounded-xl border-2 border-dashed border-neutral-200 dark:border-navy-700 cursor-pointer hover:border-pink-400 transition-all">
                          <Upload size={20} className="text-neutral-400" />
                          <span className="text-sm text-neutral-500 font-bold">Clique para enviar {msg.type === 'image' ? 'uma imagem' : msg.type === 'audio' ? 'um áudio' : 'um vídeo'}</span>
                          <input type="file" hidden accept={acceptMap[msg.type]} onChange={e => { const f = e.target.files?.[0]; if (f) handleMediaUpload(msg.id, f); e.target.value = ''; }} />
                        </label>
                      )}
                    </div>
                  )}
                </div>
              </React.Fragment>
            ))}
          </div>

          {/* Add message */}
          <div className="mt-4">
            <button onClick={addMessage} className="flex items-center gap-2 px-4 py-2.5 border-2 border-dashed border-neutral-300 dark:border-navy-600 rounded-xl text-sm font-bold text-neutral-500 hover:border-pink-400 hover:text-pink-500 transition-all">
              <Plus size={16} /> Adicionar mensagem
            </button>
          </div>
        </div>

        {/* Preview */}
        <div className="bg-white dark:bg-navy-900 border border-neutral-200 dark:border-navy-800 rounded-2xl p-6 mb-6">
          <h3 className="text-lg font-black text-navy-950 dark:text-white mb-4 flex items-center gap-2"><Phone size={18} className="text-pink-600" /> Prévia</h3>
          <div className="p-4 bg-[#efeae2] dark:bg-navy-950 rounded-xl space-y-2">
            {birthdayConfig.messages.map((msg, i) => {
              const nextMsg = birthdayConfig.messages[i + 1];
              const intMin = nextMsg?.intervalMin ?? birthdayConfig.msgIntervalMin;
              const intMax = nextMsg?.intervalMax ?? birthdayConfig.msgIntervalMax;
              return (
                <React.Fragment key={msg.id}>
                  {msg.type === 'text' && msg.content ? (
                    <div className="flex justify-end"><div className="bg-[#d9fdd3] dark:bg-emerald-900 px-4 py-2.5 rounded-xl max-w-md"><p className="text-sm text-navy-950 dark:text-white whitespace-pre-wrap">{msg.content.replace(/\{nome\}/g, 'Maria Silva')}</p></div></div>
                  ) : msg.type !== 'text' && msg.content ? (
                    <div className="flex justify-end">
                      <div className="bg-[#d9fdd3] dark:bg-emerald-900 px-4 py-2.5 rounded-xl max-w-md">
                        {msg.type === 'image' && <img src={msg.content} alt="" className="max-h-40 rounded-lg" />}
                        {msg.type === 'audio' && <div className="flex items-center gap-2"><span className="text-lg">🎙️</span><div className="flex-1 h-6 bg-emerald-200/50 dark:bg-emerald-800/30 rounded-full flex items-center px-2"><div className="flex gap-0.5">{Array.from({ length: 20 }).map((_, j) => <div key={j} className="w-0.5 bg-emerald-600/60 rounded-full" style={{ height: `${4 + Math.random() * 14}px` }} />)}</div></div><span className="text-[10px] text-neutral-500">{msg.fileName || 'Áudio'}</span></div>}
                        {msg.type === 'video' && <p className="text-sm text-navy-950 dark:text-white">🎬 <span className="italic">{msg.fileName || 'Vídeo'}</span></p>}
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-end"><div className="bg-neutral-200 dark:bg-navy-700 px-4 py-2.5 rounded-xl"><p className="text-xs text-neutral-400 italic">{msg.type === 'text' ? 'Mensagem vazia' : 'Mídia não definida'}</p></div></div>
                  )}
                  {i < birthdayConfig.messages.length - 1 && (
                    <div className="text-center py-0.5 space-y-0.5">
                      <p className="text-[10px] text-neutral-400">⏳ {intMin}s ~ {intMax}s</p>
                      {nextMsg?.showPresence && (
                        <p className="text-[10px] text-emerald-500 italic">{(nextMsg.presenceType || (nextMsg.type === 'audio' ? 'recording' : 'typing')) === 'recording' ? '🎙️ gravando áudio...' : '✏️ digitando...'}</p>
                      )}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Upcoming birthdays */}
        <div className="bg-white dark:bg-navy-900 border border-neutral-200 dark:border-navy-800 rounded-2xl p-6 mb-6">
          <h3 className="text-lg font-black text-navy-950 dark:text-white mb-1 flex items-center gap-2"><Calendar size={18} className="text-pink-600" /> Próximos Aniversários</h3>
          <p className="text-xs text-neutral-500 mb-4">Contatos com data de nascimento cadastrada.</p>
          {upcoming.length === 0 ? (
            <div className="text-center py-10"><Cake size={32} className="text-neutral-300 dark:text-navy-700 mx-auto mb-3" /><p className="text-neutral-400 text-sm">Nenhum contato com data de nascimento cadastrada.</p></div>
          ) : (
            <div className="space-y-2">
              {upcoming.map(c => {
                const isToday = c.daysUntil === 0;
                const isSoon = c.daysUntil <= 7;
                return (
                  <div key={c.id} className={`flex items-center gap-4 p-3 rounded-xl transition-all ${isToday ? 'bg-pink-50 dark:bg-pink-900/20 border border-pink-200 dark:border-pink-800' : 'hover:bg-neutral-50 dark:hover:bg-navy-800'}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isToday ? 'bg-pink-500 text-white' : 'bg-neutral-100 dark:bg-navy-700'}`}>{isToday ? <Cake size={18} /> : <UserIcon size={18} className="text-neutral-400" />}</div>
                    <div className="flex-1 min-w-0"><p className="font-bold text-sm text-navy-950 dark:text-white truncate">{c.name}</p><p className="text-xs text-neutral-400">{new Date(c.birthdate + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'long' })}</p></div>
                    <span className={`text-xs font-black px-3 py-1 rounded-full flex-shrink-0 ${isToday ? 'bg-pink-500 text-white' : isSoon ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600' : 'bg-neutral-100 dark:bg-navy-800 text-neutral-500'}`}>{isToday ? 'Hoje!' : c.daysUntil === 1 ? 'Amanhã' : `${c.daysUntil} dias`}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Save */}
        <div className="bg-white dark:bg-navy-900 border border-neutral-200 dark:border-navy-800 rounded-2xl p-6">
          <button onClick={handleSaveBirthdayConfig} disabled={savingBirthday} className="w-full py-4 bg-pink-500 hover:bg-pink-600 text-white rounded-xl font-black text-xs uppercase tracking-widest shadow-lg flex items-center justify-center gap-2 transition-all">
            {savingBirthday ? <Loader2 className="animate-spin" size={16} /> : birthdaySaved ? <><CheckCircle2 size={16} /> Salvo!</> : <><Save size={16} /> Salvar Configurações</>}
          </button>
        </div>
      </div>
    );
  }

  // ─── GROUPS VIEW ───
  if (subView === 'groups') {
    if (sessionStatus !== 'connected') {
      return (
        <div className="max-w-2xl mx-auto py-20 text-center">
          <div className="w-20 h-20 mx-auto bg-emerald-50 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mb-6"><Users className="text-emerald-600" size={36} /></div>
          <h2 className="text-3xl font-black tracking-tighter mb-2 text-navy-950 dark:text-white">Gestão de Grupos</h2>
          <p className="text-neutral-500 mb-6">Conecte o WhatsApp primeiro para gerenciar grupos.</p>
          <button onClick={() => onNavigate?.('whatsapp')} className="px-6 py-3 bg-emerald-600 text-white rounded-xl font-bold text-sm hover:bg-emerald-700 transition-all">Ir para Conversas</button>
        </div>
      );
    }

    if (selectedGroupChat) {
      return (
        <div className="flex h-[calc(100vh-120px)] bg-white dark:bg-navy-950 rounded-2xl border border-neutral-200 dark:border-navy-800 overflow-hidden shadow-xl">
          {/* Chat area */}
          <div className="flex flex-col flex-1">
            <div className="p-4 border-b border-neutral-100 dark:border-navy-800 flex items-center gap-3 bg-neutral-50 dark:bg-navy-900">
              <button onClick={() => { setSelectedGroupChat(null); setGroupInfo(null); }} className="p-1 text-neutral-400 hover:text-navy-950 dark:hover:text-white"><ArrowLeft size={20} /></button>
              <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center"><Users className="text-emerald-600" size={18} /></div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm text-navy-950 dark:text-white truncate">{formatChatName(selectedGroupChat)}</p>
                <p className="text-[10px] text-neutral-400">{groupInfo?.participants?.length || '...'} participantes</p>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-1 bg-[#efeae2] dark:bg-navy-950">
              {loadingMessages ? (
                <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-neutral-400" size={24} /></div>
              ) : messages.length === 0 ? (
                <div className="text-center py-20 text-neutral-400 text-sm">Nenhuma mensagem</div>
              ) : (
                messages.map(msg => {
                  const senderName = msg._data?.pushName;
                  const senderPhone = msg._data?.key?.participantAlt?.replace('@s.whatsapp.net', '');
                  const senderLabel = senderName || (senderPhone ? formatPhone(senderPhone) : null);
                  return (
                    <div key={msg.id} className={`flex ${msg.fromMe ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[70%] px-3 py-2 rounded-xl shadow-sm ${msg.fromMe ? 'bg-[#d9fdd3] dark:bg-emerald-900' : 'bg-white dark:bg-navy-800'}`}>
                        {!msg.fromMe && senderLabel && <p className="text-[11px] font-bold text-emerald-700 dark:text-emerald-400 mb-1">{senderLabel}</p>}
                        <p className="text-sm text-navy-950 dark:text-white whitespace-pre-wrap break-words">{msg.body}</p>
                        <p className={`text-[10px] mt-1 text-right ${msg.fromMe ? 'text-emerald-700/60' : 'text-neutral-400'}`}>{formatTime(msg.timestamp)}<AckIcon ack={msg.ack} fromMe={msg.fromMe} /></p>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>
            <div className="p-3 border-t border-neutral-100 dark:border-navy-800 bg-neutral-50 dark:bg-navy-900">
              <form onSubmit={e => { e.preventDefault(); handleSendMessage(); }} className="flex items-center gap-2">
                <input value={messageText} onChange={e => setMessageText(e.target.value)} placeholder="Mensagem para o grupo..." className="flex-1 bg-white dark:bg-navy-950 rounded-xl px-4 py-3 text-sm outline-none border border-neutral-200 dark:border-navy-700 dark:text-white" />
                <button type="submit" disabled={sendingMessage || !messageText.trim()} className="p-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-all disabled:opacity-50">
                  {sendingMessage ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
                </button>
              </form>
            </div>
          </div>
          {/* Group info sidebar */}
          <div className="hidden lg:flex flex-col w-80 border-l border-neutral-200 dark:border-navy-800 bg-neutral-50 dark:bg-navy-900 overflow-y-auto">
            <div className="p-6 border-b border-neutral-100 dark:border-navy-800 text-center">
              <div className="w-20 h-20 mx-auto bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mb-4"><Users className="text-emerald-600" size={32} /></div>
              <p className="font-black text-navy-950 dark:text-white text-lg">{formatChatName(selectedGroupChat)}</p>
              <p className="text-xs text-neutral-400 mt-1">Grupo · {groupInfo?.participants?.length || '...'} participantes</p>
            </div>
            {loadingGroupInfo ? (
              <div className="flex items-center justify-center py-10"><Loader2 className="animate-spin text-emerald-500" size={20} /></div>
            ) : groupInfo?.participants ? (
              <div className="p-4">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Participantes ({groupInfo.participants.length})</p>
                <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
                  {groupInfo.participants.map((p: any, i: number) => {
                    const pId = p.id || p;
                    const pPhone = typeof pId === 'string' ? pId.replace('@c.us', '').replace('@s.whatsapp.net', '') : '';
                    return (
                      <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white dark:hover:bg-navy-800 transition-all">
                        <div className="w-8 h-8 bg-neutral-200 dark:bg-navy-700 rounded-full flex items-center justify-center flex-shrink-0"><UserIcon size={14} className="text-neutral-400" /></div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-bold text-navy-950 dark:text-white truncate">{p.pushName || formatPhone(pPhone)}</p>
                          {p.isAdmin && <p className="text-[9px] text-emerald-600 font-bold">Admin</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="p-4 text-center text-neutral-400 text-xs">Informações do grupo indisponíveis</div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="max-w-5xl mx-auto py-10 px-4">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center"><Users className="text-emerald-600" size={22} /></div>
          <div>
            <h2 className="text-3xl font-black tracking-tighter text-navy-950 dark:text-white">Gestão de Grupos</h2>
            <p className="text-neutral-500 dark:text-slate-400 text-sm">Gerencie seus grupos, crie hubs e envie mensagens em massa.</p>
          </div>
        </div>

        {/* Dashboard stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white dark:bg-navy-900 border border-neutral-200 dark:border-navy-800 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-2"><Users size={16} className="text-emerald-600" /><span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Grupos</span></div>
            <p className="text-3xl font-black text-navy-950 dark:text-white">{groupChats.length}</p>
          </div>
          <div className="bg-white dark:bg-navy-900 border border-neutral-200 dark:border-navy-800 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-2"><Link2 size={16} className="text-blue-600" /><span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Hubs</span></div>
            <p className="text-3xl font-black text-navy-950 dark:text-white">{groupHubs.length}</p>
          </div>
          <div className="bg-white dark:bg-navy-900 border border-neutral-200 dark:border-navy-800 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-2"><Send size={16} className="text-violet-600" /><span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Mensagens</span></div>
            <p className="text-3xl font-black text-navy-950 dark:text-white">{groupMessages.length}</p>
          </div>
          <div className="bg-white dark:bg-navy-900 border border-neutral-200 dark:border-navy-800 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-2"><UserIcon size={16} className="text-amber-600" /><span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Participantes</span></div>
            <p className="text-3xl font-black text-navy-950 dark:text-white">{groupMessages.reduce((sum, m) => sum + (m.total_participants || 0), 0)}</p>
          </div>
        </div>

        {/* Hub creator */}
        <div className="bg-white dark:bg-navy-900 border border-neutral-200 dark:border-navy-800 rounded-2xl p-6 mb-6">
          <h3 className="text-lg font-black text-navy-950 dark:text-white mb-4 flex items-center gap-2"><Link2 size={18} className="text-blue-600" /> {editingHub ? 'Editar Hub' : 'Novo Hub de Grupos'}</h3>
          <p className="text-xs text-neutral-500 mb-4">Agrupe vários grupos para envio de mensagens em massa.</p>
          <div className="flex gap-3 mb-3">
            <input value={hubName} onChange={e => setHubName(e.target.value)} placeholder="Nome do hub" className="flex-1 bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-700 rounded-xl px-4 py-3 font-bold text-sm outline-none dark:text-white" />
            <input value={hubDescription} onChange={e => setHubDescription(e.target.value)} placeholder="Descrição (opcional)" className="flex-1 bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-700 rounded-xl px-4 py-3 font-bold text-sm outline-none dark:text-white" />
          </div>
          <div className="flex items-center gap-2">
            {editingHub && <button onClick={() => { setEditingHub(null); setHubName(''); setHubDescription(''); }} className="px-4 py-2.5 rounded-xl text-sm font-bold text-neutral-500 hover:text-neutral-700">Cancelar</button>}
            <button onClick={handleSaveHub} disabled={savingHub || !hubName.trim()} className="flex items-center gap-2 px-5 py-2.5 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white rounded-xl text-sm font-black transition-all">
              {savingHub ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} {editingHub ? 'Salvar' : 'Criar Hub'}
            </button>
          </div>
        </div>

        {/* Existing hubs */}
        {groupHubs.length > 0 && (
          <div className="bg-white dark:bg-navy-900 border border-neutral-200 dark:border-navy-800 rounded-2xl p-6 mb-6">
            <h3 className="text-lg font-black text-navy-950 dark:text-white mb-4 flex items-center gap-2"><Link2 size={18} className="text-blue-600" /> Hubs de Grupos</h3>
            <div className="space-y-4">
              {groupHubs.map(hub => {
                const members = groupHubMembers[hub.id] || [];
                const isExpanded = selectedHubId === hub.id;
                return (
                  <div key={hub.id} className="border border-neutral-200 dark:border-navy-700 rounded-xl overflow-hidden">
                    <div className="p-4 flex items-center justify-between cursor-pointer hover:bg-neutral-50 dark:hover:bg-navy-800 transition-all" onClick={() => setSelectedHubId(isExpanded ? null : hub.id)}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center"><Link2 size={18} className="text-blue-600" /></div>
                        <div>
                          <p className="font-black text-navy-950 dark:text-white">{hub.name}</p>
                          <p className="text-xs text-neutral-400">{members.length} grupo{members.length !== 1 ? 's' : ''}{hub.description ? ` · ${hub.description}` : ''}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={e => { e.stopPropagation(); setEditingHub(hub); setHubName(hub.name); setHubDescription(hub.description || ''); }} className="p-2 text-neutral-400 hover:text-blue-500 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all"><Edit2 size={14} /></button>
                        <button onClick={e => { e.stopPropagation(); handleDeleteHub(hub.id); }} className="p-2 text-neutral-400 hover:text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"><Trash2 size={14} /></button>
                        <ChevronDown size={16} className={`text-neutral-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="border-t border-neutral-200 dark:border-navy-700 p-4 space-y-4">
                        {/* Add groups to hub */}
                        <div>
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Adicionar grupos</p>
                          <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                            {groupChats.filter(g => !members.some(m => m.group_jid === g.id)).map(g => (
                              <button key={g.id} onClick={() => handleAddGroupToHub(hub.id, g.id, formatChatName(g))}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-neutral-100 dark:bg-navy-800 text-neutral-600 dark:text-slate-300 rounded-lg text-xs font-bold hover:bg-blue-100 dark:hover:bg-blue-900/30 hover:text-blue-600 transition-all">
                                <Plus size={12} /> {formatChatName(g)}
                              </button>
                            ))}
                            {groupChats.filter(g => !members.some(m => m.group_jid === g.id)).length === 0 && (
                              <p className="text-xs text-neutral-400 italic">Todos os grupos já foram adicionados</p>
                            )}
                          </div>
                        </div>
                        {/* Current members */}
                        {members.length > 0 && (
                          <div>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Grupos no hub</p>
                            <div className="space-y-1.5">
                              {members.map(m => (
                                <div key={m.id} className="flex items-center justify-between px-3 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                                  <div className="flex items-center gap-2">
                                    <Users size={14} className="text-blue-600" />
                                    <span className="text-sm font-bold text-navy-950 dark:text-white">{m.group_name || m.group_jid}</span>
                                  </div>
                                  <button onClick={() => handleRemoveGroupFromHub(hub.id, m.group_jid)} className="p-1 text-neutral-400 hover:text-red-500 transition-all"><X size={14} /></button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {/* Send flow to hub */}
                        {members.length > 0 && (
                          <div>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Fluxo de mensagens para todos os grupos</p>
                            <div className="space-y-3">
                              {hubFlowMessages.map((msg, i) => (
                                <React.Fragment key={msg.id}>
                                  {i > 0 && (
                                    <div className="flex flex-col items-center gap-1 py-1">
                                      <div className="h-3 border-l-2 border-dashed border-neutral-300 dark:border-navy-600" />
                                      <div className="flex items-center gap-2 px-3 py-1.5 bg-neutral-100 dark:bg-navy-800 rounded-xl">
                                        <Clock size={11} className="text-neutral-400" />
                                        <span className="text-[10px] font-bold text-neutral-400">aguardar</span>
                                        <input type="number" min={1} max={600} value={msg.intervalMin ?? 10} onChange={e => hubFlowUpdateMessage(msg.id, { intervalMin: Number(e.target.value) })}
                                          className="w-12 bg-white dark:bg-navy-950 border border-neutral-200 dark:border-navy-700 rounded-md px-1 py-0.5 text-[10px] font-bold text-center outline-none dark:text-white" />
                                        <span className="text-[10px] text-neutral-400">a</span>
                                        <input type="number" min={1} max={600} value={msg.intervalMax ?? 20} onChange={e => hubFlowUpdateMessage(msg.id, { intervalMax: Number(e.target.value) })}
                                          className="w-12 bg-white dark:bg-navy-950 border border-neutral-200 dark:border-navy-700 rounded-md px-1 py-0.5 text-[10px] font-bold text-center outline-none dark:text-white" />
                                        <span className="text-[10px] text-neutral-500 font-bold">seg</span>
                                      </div>
                                      <button onClick={() => hubFlowUpdateMessage(msg.id, { showPresence: !msg.showPresence })}
                                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all ${msg.showPresence ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600' : 'bg-neutral-50 dark:bg-navy-900 text-neutral-400 hover:text-neutral-600'}`}>
                                        {msg.showPresence ? '✓' : '+'} {msg.type === 'audio' ? 'Gravando áudio...' : 'Digitando...'}
                                      </button>
                                      <div className="h-3 border-l-2 border-dashed border-neutral-300 dark:border-navy-600" />
                                    </div>
                                  )}
                                  <div className="border border-neutral-200 dark:border-navy-700 rounded-xl p-3">
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="flex items-center gap-2">
                                        <span className="text-[9px] font-black text-slate-400 uppercase">Msg {i + 1}</span>
                                        <select value={msg.type} onChange={e => hubFlowUpdateMessage(msg.id, { type: e.target.value as any, content: '', fileName: undefined })}
                                          className="bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-700 rounded-lg px-2 py-1 text-xs font-bold outline-none dark:text-white">
                                          <option value="text">Texto</option><option value="image">Imagem</option><option value="audio">Áudio</option><option value="video">Vídeo</option>
                                        </select>
                                      </div>
                                      {hubFlowMessages.length > 1 && <button onClick={() => hubFlowRemoveMessage(msg.id)} className="p-1 text-neutral-400 hover:text-red-500"><Trash2 size={12} /></button>}
                                    </div>
                                    {msg.type === 'text' ? (
                                      <textarea value={msg.content} onChange={e => hubFlowUpdateMessage(msg.id, { content: e.target.value })} rows={2} placeholder="Mensagem..."
                                        className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-700 rounded-lg px-3 py-2 text-sm outline-none dark:text-white resize-none" />
                                    ) : uploadingHubMedia === msg.id ? (
                                      <div className="flex items-center gap-2 p-3 bg-slate-50 dark:bg-navy-950 rounded-lg"><Loader2 className="animate-spin text-blue-500" size={16} /><span className="text-xs text-neutral-400">Enviando...</span></div>
                                    ) : msg.content ? (
                                      <div className="flex items-center gap-2 p-2 bg-slate-50 dark:bg-navy-950 rounded-lg">
                                        <span className="text-sm dark:text-white font-bold truncate flex-1">{msg.fileName || 'Arquivo'}</span>
                                        <button onClick={() => hubFlowUpdateMessage(msg.id, { content: '', fileName: undefined })} className="text-neutral-400 hover:text-red-500"><X size={12} /></button>
                                      </div>
                                    ) : (
                                      <label className="flex items-center gap-2 p-3 bg-slate-50 dark:bg-navy-950 rounded-lg border-2 border-dashed border-neutral-200 dark:border-navy-700 cursor-pointer hover:border-blue-400 transition-all">
                                        <Upload size={16} className="text-neutral-400" /><span className="text-xs text-neutral-500 font-bold">Enviar arquivo</span>
                                        <input type="file" hidden accept={msg.type === 'image' ? 'image/*' : msg.type === 'audio' ? 'audio/*' : 'video/*'} onChange={e => { const f = e.target.files?.[0]; if (f) hubFlowHandleMediaUpload(msg.id, f); e.target.value = ''; }} />
                                      </label>
                                    )}
                                  </div>
                                </React.Fragment>
                              ))}
                            </div>
                            <div className="mt-3 flex items-center justify-between">
                              <button onClick={hubFlowAddMessage} className="flex items-center gap-1.5 px-3 py-2 border-2 border-dashed border-neutral-300 dark:border-navy-600 rounded-lg text-xs font-bold text-neutral-500 hover:border-blue-400 hover:text-blue-500 transition-all">
                                <Plus size={14} /> Mensagem
                              </button>
                              <button onClick={() => handleSendFlowToHub(hub.id)} disabled={sendingHubFlow || hubFlowMessages.every(m => !m.content.trim())}
                                className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-xl font-black text-xs transition-all">
                                {sendingHubFlow ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} Disparar para {members.length} grupo{members.length !== 1 ? 's' : ''}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Group list */}
        <div className="bg-white dark:bg-navy-900 border border-neutral-200 dark:border-navy-800 rounded-2xl p-6 mb-6">
          <h3 className="text-lg font-black text-navy-950 dark:text-white mb-4 flex items-center gap-2"><Users size={18} className="text-emerald-600" /> Seus Grupos ({groupChats.length})</h3>
          {groupChats.length === 0 ? (
            <div className="text-center py-10"><Users size={32} className="text-neutral-300 dark:text-navy-700 mx-auto mb-3" /><p className="text-neutral-400 text-sm">Nenhum grupo encontrado.</p></div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {groupChats.map(g => (
                <button key={g.id} onClick={() => { setSelectedGroupChat(g); setSelectedChat(g); fetchMessages(g.id); fetchGroupInfo(g.id); }}
                  className="flex items-center gap-3 p-4 border border-neutral-200 dark:border-navy-700 rounded-xl hover:bg-emerald-50 dark:hover:bg-emerald-900/10 transition-all text-left">
                  <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center flex-shrink-0"><Users className="text-emerald-600" size={18} /></div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm text-navy-950 dark:text-white truncate">{formatChatName(g)}</p>
                    {g.lastMessage?.body && <p className="text-xs text-neutral-400 truncate">{g.lastMessage.body.slice(0, 30)}</p>}
                  </div>
                  {(g.unreadCount || 0) > 0 && (
                    <span className="w-5 h-5 bg-emerald-500 text-white text-[9px] font-black rounded-full flex items-center justify-center flex-shrink-0">{(g.unreadCount || 0) > 9 ? '9+' : g.unreadCount}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Recent hub messages */}
        {groupMessages.length > 0 && (
          <div className="bg-white dark:bg-navy-900 border border-neutral-200 dark:border-navy-800 rounded-2xl p-6">
            <h3 className="text-lg font-black text-navy-950 dark:text-white mb-4 flex items-center gap-2"><BarChart3 size={18} className="text-violet-600" /> Mensagens Enviadas</h3>
            <div className="space-y-3">
              {groupMessages.slice(0, 10).map(gm => {
                const hub = groupHubs.find(h => h.id === gm.hub_id);
                return (
                  <div key={gm.id} className="border border-neutral-200 dark:border-navy-700 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-bold text-sm text-navy-950 dark:text-white">{hub?.name || 'Envio direto'}</p>
                        <p className="text-[10px] text-neutral-400">{new Date(gm.sent_at).toLocaleString('pt-BR')}</p>
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="flex items-center gap-1 text-emerald-600 font-bold"><Users size={12} /> {gm.total_groups} grupo{gm.total_groups !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                    <p className="text-sm text-neutral-600 dark:text-slate-300 bg-neutral-50 dark:bg-navy-950 rounded-lg px-3 py-2">{gm.message_content?.text?.slice(0, 150) || '...'}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─── BROADCASTS MONITOR VIEW ───
  if (subView === 'broadcasts') {
    if (sessionStatus !== 'connected') {
      return (
        <div className="max-w-2xl mx-auto py-20 text-center">
          <div className="w-20 h-20 mx-auto bg-violet-50 dark:bg-violet-900/30 rounded-full flex items-center justify-center mb-6"><Radio className="text-violet-600" size={36} /></div>
          <h2 className="text-3xl font-black tracking-tighter mb-2 text-navy-950 dark:text-white">Listas de Transmissão</h2>
          <p className="text-neutral-500 mb-6">Conecte o WhatsApp primeiro para monitorar suas listas.</p>
          <button onClick={() => onNavigate?.('whatsapp')} className="px-6 py-3 bg-emerald-600 text-white rounded-xl font-bold text-sm hover:bg-emerald-700 transition-all">Ir para Conversas</button>
        </div>
      );
    }

    return (
      <div className="max-w-4xl mx-auto py-10 px-4">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-violet-100 dark:bg-violet-900/30 rounded-xl flex items-center justify-center"><Radio className="text-violet-600" size={22} /></div>
            <div>
              <h2 className="text-3xl font-black tracking-tighter text-navy-950 dark:text-white">Listas de Transmissão</h2>
              <p className="text-neutral-500 dark:text-slate-400 text-sm">Monitore mensagens enviadas pelas suas listas de transmissão.</p>
            </div>
          </div>
          <button onClick={fetchBroadcastData} className="p-2 text-neutral-400 hover:text-navy-950 dark:hover:text-white rounded-lg hover:bg-neutral-100 dark:hover:bg-navy-800 transition-all"><RefreshCw size={18} /></button>
        </div>

        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-2xl p-4 mb-6 flex items-start gap-3">
          <Info size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs text-amber-700 dark:text-amber-400">O sistema escaneia seus chats recentes buscando mensagens de broadcast. Pode demorar alguns segundos.</p>
            <button onClick={async () => {
              try {
                const data = await edgeFn('diagnose');
                const pre = document.createElement('pre');
                pre.textContent = JSON.stringify(data, null, 2);
                pre.style.cssText = 'max-height:80vh;overflow:auto;font-size:11px;padding:16px;background:#1a1a2e;color:#0f0;border-radius:12px;position:fixed;top:5vh;left:5vw;width:90vw;z-index:9999;';
                const close = document.createElement('button');
                close.textContent = 'X Fechar';
                close.style.cssText = 'position:fixed;top:6vh;right:6vw;z-index:10000;background:#f00;color:#fff;border:none;padding:8px 16px;border-radius:8px;font-weight:bold;cursor:pointer;';
                close.onclick = () => { pre.remove(); close.remove(); };
                document.body.append(pre, close);
              } catch (e: any) { alert('Erro: ' + e.message); }
            }} className="mt-1 text-[10px] font-bold text-amber-600 hover:underline">Diagnóstico</button>
          </div>
        </div>

        {loadingBroadcasts ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Loader2 className="animate-spin text-violet-500" size={28} />
            <p className="text-xs text-neutral-400 font-bold">Escaneando chats para encontrar broadcasts...</p>
          </div>
        ) : !broadcastData || broadcastData.messages.length === 0 ? (
          <div className="bg-white dark:bg-navy-900 border border-neutral-200 dark:border-navy-800 rounded-2xl p-10 text-center">
            <Radio size={40} className="text-neutral-300 dark:text-navy-700 mx-auto mb-4" />
            <p className="font-bold text-navy-950 dark:text-white mb-2">Nenhuma mensagem de broadcast encontrada</p>
            <p className="text-sm text-neutral-400 max-w-md mx-auto">Envie uma mensagem por uma lista de transmissão no celular. O sistema detecta automaticamente nos chats individuais.</p>
            {broadcastData && <p className="text-[10px] text-neutral-400 mt-3">{(broadcastData as any).chatsScanned || 0} chats escaneados</p>}
          </div>
        ) : (
          <div>
            <div className="flex items-center gap-4 mb-6">
              <span className="text-sm font-bold text-navy-950 dark:text-white">{broadcastData.messages.length} mensage{broadcastData.messages.length !== 1 ? 'ns' : 'm'}</span>
              <span className="text-sm text-neutral-400">·</span>
              <span className="text-sm font-bold text-violet-600">{broadcastData.totalRecipients} destinatário{broadcastData.totalRecipients !== 1 ? 's' : ''}</span>
              <span className="text-sm text-neutral-400">·</span>
              <span className="text-[10px] text-neutral-400">{(broadcastData as any).chatsScanned || 0} chats escaneados</span>
            </div>
            <div className="space-y-4">
              {broadcastData.messages.map((msg, gi) => {
                const delivered = msg.recipients.filter(r => r.ack >= 2).length;
                const read = msg.recipients.filter(r => r.ack >= 3).length;
                const total = msg.recipients.length;

                return (
                  <div key={gi} className="bg-white dark:bg-navy-900 border border-neutral-200 dark:border-navy-800 rounded-2xl overflow-hidden">
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[10px] text-neutral-400 font-bold">{formatDate(msg.timestamp)} {formatTime(msg.timestamp)}</p>
                        <div className="flex items-center gap-3">
                          <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-600"><CheckCheck size={11} /> {delivered}/{total}</span>
                          <span className="flex items-center gap-1 text-[10px] font-bold text-blue-600"><Eye size={11} /> {read}/{total}</span>
                        </div>
                      </div>
                      <p className="text-sm text-navy-950 dark:text-white whitespace-pre-wrap mb-1">{(msg.body || '').slice(0, 300)}</p>
                      {total > 0 && (
                        <div className="mt-3 pt-3 border-t border-neutral-100 dark:border-navy-800 space-y-1.5">
                          {msg.recipients.map((r, ri) => (
                            <div key={ri} className="flex items-center justify-between px-3 py-2 bg-neutral-50 dark:bg-navy-950 rounded-lg">
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 bg-neutral-200 dark:bg-navy-700 rounded-full flex items-center justify-center"><UserIcon size={12} className="text-neutral-400" /></div>
                                <span className="text-sm font-bold text-navy-950 dark:text-white">{r.name}</span>
                                {r.name !== r.phone && <span className="text-[10px] text-neutral-400">{r.phone}</span>}
                              </div>
                              <span className={`flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[9px] font-black ${
                                r.ack >= 3 ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600' :
                                r.ack >= 2 ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600' :
                                r.ack >= 1 ? 'bg-neutral-100 dark:bg-navy-800 text-neutral-500' :
                                'bg-neutral-100 dark:bg-navy-800 text-neutral-400'
                              }`}>
                                {r.ack >= 3 ? <><CheckCheck size={10} className="text-blue-500" /> Lida</> :
                                 r.ack >= 2 ? <><CheckCheck size={10} /> Entregue</> :
                                 r.ack >= 1 ? <><Check size={10} /> Enviada</> :
                                 <><Clock size={10} /> Pendente</>}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─── LOADING SKELETON ───
  if (loading) {
    return (
      <div className="flex h-[calc(100vh-120px)] bg-white dark:bg-navy-950 rounded-2xl border border-neutral-200 dark:border-navy-800 overflow-hidden shadow-xl">
        <div className="flex flex-col w-full md:w-96 border-r border-neutral-200 dark:border-navy-800">
          <div className="p-4 border-b border-neutral-100 dark:border-navy-800 flex items-center gap-3 bg-neutral-50 dark:bg-navy-900">
            <div className="w-10 h-10 bg-neutral-200 dark:bg-navy-700 rounded-full animate-pulse" />
            <div className="flex-1"><div className="h-4 bg-neutral-200 dark:bg-navy-700 rounded w-24 mb-1.5 animate-pulse" /><div className="h-3 bg-neutral-100 dark:bg-navy-800 rounded w-32 animate-pulse" /></div>
          </div>
          <div className="p-3"><div className="h-10 bg-neutral-100 dark:bg-navy-800 rounded-xl animate-pulse" /></div>
          <div className="flex-1">
            {[100, 75, 90, 60, 85, 70, 95].map((w, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3 border-b border-neutral-50 dark:border-navy-900">
                <div className="w-12 h-12 bg-neutral-200 dark:bg-navy-700 rounded-full animate-pulse flex-shrink-0" />
                <div className="flex-1">
                  <div className="h-3.5 bg-neutral-200 dark:bg-navy-700 rounded mb-2 animate-pulse" style={{ width: `${w}%` }} />
                  <div className="h-3 bg-neutral-100 dark:bg-navy-800 rounded animate-pulse" style={{ width: `${w - 20}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="hidden md:flex flex-1 items-center justify-center bg-neutral-50 dark:bg-navy-900/50">
          <div className="text-center"><div className="w-20 h-20 mx-auto bg-neutral-100 dark:bg-navy-800 rounded-full mb-4 animate-pulse" /><div className="h-4 bg-neutral-200 dark:bg-navy-700 rounded w-40 mx-auto animate-pulse" /></div>
        </div>
      </div>
    );
  }

  // ─── DISCONNECTED ───
  if (sessionStatus === 'disconnected' || sessionStatus === 'failed') {
    return (
      <div className="max-w-2xl mx-auto py-20 text-center">
        <div className="w-24 h-24 mx-auto bg-emerald-50 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mb-8">
          <MessageSquare className="text-emerald-600" size={40} />
        </div>
        <h2 className="text-4xl font-black tracking-tighter mb-4 text-navy-950 dark:text-white">WhatsApp</h2>
        <p className="text-neutral-500 dark:text-slate-400 mb-2 max-w-md mx-auto">Conecte o WhatsApp do seu gabinete para receber lembretes de tarefas e interagir com a equipe.</p>
        {sessionStatus === 'failed' && (
          <div className="mb-4">
            <p className="text-red-500 text-sm font-bold mb-2">Falha na conexão.</p>
            <button onClick={async () => { setActionLoading(true); try { await edgeFn('cleanup', { method: 'POST' }); setSessionStatus('disconnected'); } catch {} setActionLoading(false); }}
              className="text-xs font-bold text-neutral-500 hover:text-red-500 underline">Limpar sessões e recomeçar</button>
          </div>
        )}
        <button onClick={async () => {
          try {
            const data = await edgeFn('diagnose');
            const pre = document.createElement('pre');
            pre.textContent = JSON.stringify(data, null, 2);
            pre.style.cssText = 'max-height:80vh;overflow:auto;font-size:11px;padding:16px;background:#1a1a2e;color:#0f0;border-radius:12px;position:fixed;top:5vh;left:5vw;width:90vw;z-index:9999;';
            const close = document.createElement('button');
            close.textContent = 'X Fechar';
            close.style.cssText = 'position:fixed;top:6vh;right:6vw;z-index:10000;background:#f00;color:#fff;border:none;padding:8px 16px;border-radius:8px;font-weight:bold;cursor:pointer;';
            close.onclick = () => { pre.remove(); close.remove(); };
            document.body.append(pre, close);
          } catch (e: any) { alert('Erro: ' + e.message); }
        }} className="mt-4 text-xs font-bold text-neutral-400 hover:text-neutral-600 underline">Diagnóstico da sessão</button>

        <button onClick={() => handleStart()} disabled={actionLoading} className="mt-8 px-8 py-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl font-black text-sm uppercase tracking-widest shadow-xl transition-all flex items-center gap-3 mx-auto">
          {actionLoading ? <Loader2 className="animate-spin" size={20} /> : <QrCode size={20} />} Conectar WhatsApp
        </button>
      </div>
    );
  }

  // ─── QR CODE ───
  if (sessionStatus === 'qr_pending' || sessionStatus === 'connecting') {
    return (
      <div className="max-w-md mx-auto py-20 text-center">
        <div className="w-20 h-20 mx-auto bg-emerald-50 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mb-8">
          <QrCode className="text-emerald-600" size={36} />
        </div>
        <h2 className="text-3xl font-black tracking-tighter mb-2 text-navy-950 dark:text-white">Escaneie o QR Code</h2>
        <p className="text-neutral-500 dark:text-slate-400 text-sm mb-8">Abra o WhatsApp no seu celular → Menu (⋮) → Aparelhos conectados → Conectar</p>
        {qrCode ? (
          <div className="bg-white p-6 rounded-3xl shadow-xl inline-block mb-6"><img src={qrCode} alt="QR Code" className="w-64 h-64" /></div>
        ) : (
          <div className="w-64 h-64 mx-auto bg-neutral-100 dark:bg-navy-800 rounded-3xl flex items-center justify-center mb-6"><Loader2 className="animate-spin text-neutral-400" size={32} /></div>
        )}
        <div className="flex items-center justify-center gap-4">
          <button onClick={() => fetchQrCode()} className="px-6 py-3 bg-neutral-100 dark:bg-navy-800 text-neutral-600 dark:text-slate-300 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-neutral-200 transition-all flex items-center gap-2"><RefreshCw size={16} /> Atualizar QR</button>
          <button onClick={handleDisconnect} className="px-6 py-3 bg-red-50 dark:bg-red-900/20 text-red-600 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-red-100 transition-all flex items-center gap-2"><X size={16} /> Cancelar</button>
        </div>
        {sessionStatus === 'connecting' && <p className="mt-6 text-emerald-600 text-sm font-bold flex items-center justify-center gap-2"><Loader2 className="animate-spin" size={16} /> Conectando...</p>}
      </div>
    );
  }

  // ─── CONNECTED: WHATSAPP WEB VIEW ───
  return (
    <div className="flex h-[calc(100vh-120px)] bg-white dark:bg-navy-950 rounded-2xl border border-neutral-200 dark:border-navy-800 overflow-hidden shadow-xl">
      {/* ═══ LEFT: Chat List ═══ */}
      <div className={`${selectedChat ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-96 border-r border-neutral-200 dark:border-navy-800`}>
        {/* Header */}
        <div className="p-4 border-b border-neutral-100 dark:border-navy-800 flex items-center justify-between bg-neutral-50 dark:bg-navy-900">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center"><Phone className="text-white" size={18} /></div>
            <div><p className="font-black text-sm text-navy-950 dark:text-white">Conversas Individuais</p><p className="text-[10px] font-mono text-emerald-600">{phoneNumber}</p></div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => fetchChats()} className="p-2 text-neutral-400 hover:text-navy-950 dark:hover:text-white rounded-lg hover:bg-neutral-100 dark:hover:bg-navy-800 transition-all"><RefreshCw size={16} /></button>
            <button onClick={() => onNavigate?.('whatsapp-settings')} className="p-2 text-neutral-400 hover:text-navy-950 dark:hover:text-white rounded-lg hover:bg-neutral-100 dark:hover:bg-navy-800 transition-all"><Settings2 size={16} /></button>
          </div>
        </div>

        {/* Tabs - hidden on main (individual only) */}
        {subView !== 'main' && (
          <div className="flex border-b border-neutral-100 dark:border-navy-800 bg-neutral-50 dark:bg-navy-900">
            {([['all', 'Todos'], ['contacts', 'Contatos'], ['groups', 'Grupos']] as const).map(([tab, label]) => (
              <button key={tab} onClick={() => setChatTab(tab)} className={`flex-1 py-2.5 text-[10px] font-black uppercase tracking-widest transition-all border-b-2 ${chatTab === tab ? 'text-emerald-600 border-emerald-600' : 'text-neutral-400 border-transparent hover:text-neutral-600'}`}>
                {label}
              </button>
            ))}
          </div>
        )}

        {/* Search */}
        <div className="p-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" size={16} />
            <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Buscar conversa..." className="w-full bg-neutral-100 dark:bg-navy-900 rounded-xl pl-10 pr-4 py-2.5 text-sm outline-none dark:text-white placeholder-neutral-400" />
          </div>
        </div>

        {/* Chat list */}
        <div className="flex-1 overflow-y-auto">
          {loadingChats || syncingChats ? (
            <div className="flex flex-col items-center justify-center py-10 gap-3">
              <Loader2 className="animate-spin text-emerald-500" size={24} />
              <p className="text-xs font-bold text-neutral-400">{syncingChats ? 'Sincronizando...' : 'Carregando...'}</p>
            </div>
          ) : filteredChats.length === 0 ? (
            <div className="text-center py-10 px-4">
              <p className="text-neutral-400 text-sm mb-3">Nenhuma conversa</p>
              <button onClick={() => fetchChats()} className="text-xs font-bold text-emerald-600 hover:underline flex items-center gap-1 mx-auto mb-3"><RefreshCw size={12} /> Atualizar</button>
              <button onClick={async () => {
                try {
                  const data = await edgeFn('diagnose');
                  const pre = document.createElement('pre');
                  pre.textContent = JSON.stringify(data, null, 2);
                  pre.style.cssText = 'max-height:80vh;overflow:auto;font-size:11px;padding:16px;background:#1a1a2e;color:#0f0;border-radius:12px;position:fixed;top:5vh;left:5vw;width:90vw;z-index:9999;';
                  const close = document.createElement('button');
                  close.textContent = 'X Fechar';
                  close.style.cssText = 'position:fixed;top:6vh;right:6vw;z-index:10000;background:#f00;color:#fff;border:none;padding:8px 16px;border-radius:8px;font-weight:bold;cursor:pointer;';
                  close.onclick = () => { pre.remove(); close.remove(); };
                  document.body.append(pre, close);
                } catch (e: any) { alert('Erro: ' + e.message); }
              }} className="text-[10px] font-bold text-neutral-400 hover:text-neutral-600 underline">Diagnóstico</button>
            </div>
          ) : (
            filteredChats.map(chat => {
              const dbUnread = unreadCounts[chat.id] || 0;
              const hasUnread = dbUnread > 0 || (chat.unreadCount || 0) > 0;
              const unreadNum = dbUnread || chat.unreadCount || 0;
              return (
                <button key={chat.id} onClick={() => {
                  setSelectedChat(chat); fetchMessages(chat.id);
                  if (dbUnread > 0) {
                    edgeFn('mark-read', { method: 'POST', body: { chatId: chat.id } }).catch(() => {});
                    setUnreadCounts(prev => { const n = { ...prev }; delete n[chat.id]; return n; });
                  }
                }}
                  className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-neutral-50 dark:hover:bg-navy-900 transition-all border-b border-neutral-50 dark:border-navy-900 ${selectedChat?.id === chat.id ? 'bg-emerald-50 dark:bg-emerald-900/20' : ''}`}>
                  <div className="relative w-12 h-12 rounded-full flex-shrink-0">
                    {chat.picture ? (
                      <img src={chat.picture.startsWith('http') ? chat.picture : chat.picture.startsWith('data:') ? chat.picture : `data:image/jpeg;base64,${chat.picture}`} alt="" className="w-12 h-12 rounded-full object-cover" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden'); }} />
                    ) : null}
                    <div className={`w-12 h-12 bg-neutral-200 dark:bg-navy-700 rounded-full flex items-center justify-center ${chat.picture ? 'hidden' : ''}`}>
                      {isGroup(chat) ? <Users className="text-neutral-500 dark:text-slate-400" size={20} /> : <UserIcon className="text-neutral-500 dark:text-slate-400" size={20} />}
                    </div>
                    {hasUnread && <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white dark:border-navy-950" />}
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="flex items-center justify-between">
                      <p className={`font-bold text-sm truncate ${hasUnread ? 'text-navy-950 dark:text-white' : 'text-navy-950 dark:text-white'}`}>{formatChatName(chat)}</p>
                      {chat.lastMessage?.timestamp && (
                        <span className={`text-[10px] flex-shrink-0 ml-2 ${hasUnread ? 'text-emerald-600 font-bold' : 'text-neutral-400'}`}>{formatDate(chat.lastMessage.timestamp)}</span>
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-0.5">
                      {chat.lastMessage?.body ? (
                        <p className={`text-xs truncate pr-2 ${hasUnread ? 'text-navy-950 dark:text-white font-semibold' : 'text-neutral-500 dark:text-slate-400'}`}>{chat.lastMessage.body.slice(0, 40)}</p>
                      ) : <p className="text-xs text-neutral-400 italic">—</p>}
                      {hasUnread && (
                        <span className="w-5 h-5 bg-emerald-500 text-white text-[9px] font-black rounded-full flex items-center justify-center flex-shrink-0">
                          {unreadNum > 9 ? '9+' : unreadNum}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* ═══ CENTER: Messages ═══ */}
      <div className={`${selectedChat ? 'flex' : 'hidden md:flex'} flex-col flex-1`}>
        {selectedChat ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b border-neutral-100 dark:border-navy-800 flex items-center gap-3 bg-neutral-50 dark:bg-navy-900">
              <button onClick={() => setSelectedChat(null)} className="md:hidden p-1 text-neutral-400 hover:text-navy-950 dark:hover:text-white"><ArrowLeft size={20} /></button>
              <div className="relative w-10 h-10 rounded-full flex-shrink-0">
                <div className="w-10 h-10 bg-neutral-200 dark:bg-navy-700 rounded-full flex items-center justify-center">
                  {isGroup(selectedChat) ? <Users className="text-neutral-500" size={18} /> : <UserIcon className="text-neutral-500" size={18} />}
                </div>
                {selectedChat.picture && (
                  <img src={selectedChat.picture.startsWith('http') ? selectedChat.picture : selectedChat.picture.startsWith('data:') ? selectedChat.picture : `data:image/jpeg;base64,${selectedChat.picture}`} alt="" className="absolute inset-0 w-10 h-10 rounded-full object-cover" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm text-navy-950 dark:text-white truncate">{formatChatName(selectedChat)}</p>
                <p className="text-[10px] text-neutral-400 font-mono">{selectedChat.id.replace('@s.whatsapp.net', '').replace('@g.us', '')}</p>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-1 bg-[#efeae2] dark:bg-navy-950">
              {loadingMessages ? (
                <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-neutral-400" size={24} /></div>
              ) : messages.length === 0 ? (
                <div className="text-center py-20 text-neutral-400 text-sm">Nenhuma mensagem</div>
              ) : (
                messages.map(msg => {
                  const isGrp = selectedChat?.id?.includes('@g.us');
                  const senderName = msg._data?.pushName;
                  const senderPhone = msg._data?.key?.participantAlt?.replace('@s.whatsapp.net', '');
                  const senderLabel = senderName || (senderPhone ? formatPhone(senderPhone) : null);

                  const msgType = msg.type || msg._data?.type || '';
                  const mediaSrc = msg.mediaUrl || msg.media?.url || '';
                  const mimetype = msg.media?.mimetype || msg._data?.mimetype || '';
                  const caption = msg._data?.caption || msg.body || '';
                  const isSticker = msgType === 'sticker';
                  const isImage = msgType === 'image' || (mimetype.startsWith('image/') && !isSticker);
                  const isAudio = msgType === 'audio' || msgType === 'ptt' || mimetype.startsWith('audio/');
                  const isVideo = msgType === 'video' || mimetype.startsWith('video/');
                  const isDoc = msgType === 'document';
                  const hasMedia = msg.hasMedia || !!mediaSrc;

                  return (
                    <div key={msg.id} className={`flex ${msg.fromMe ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[70%] px-3 py-2 rounded-xl shadow-sm ${msg.fromMe ? 'bg-[#d9fdd3] dark:bg-emerald-900' : 'bg-white dark:bg-navy-800'}`}>
                        {isGrp && !msg.fromMe && senderLabel && (
                          <p className="text-[11px] font-bold text-emerald-700 dark:text-emerald-400 mb-1">{senderLabel}</p>
                        )}
                        {isSticker ? (
                          <p className="text-xs text-neutral-400 italic py-1">Sticker</p>
                        ) : isImage && mediaSrc ? (
                          <img src={mediaSrc} alt="" className="max-w-full rounded-lg mb-1 cursor-pointer" onClick={() => window.open(mediaSrc, '_blank')} />
                        ) : isAudio && mediaSrc ? (
                          <audio controls className="max-w-full mb-1" preload="none">
                            <source src={mediaSrc} type={mimetype || 'audio/ogg'} />
                          </audio>
                        ) : isVideo && mediaSrc ? (
                          <video controls className="max-w-full rounded-lg mb-1" preload="metadata">
                            <source src={mediaSrc} type={mimetype || 'video/mp4'} />
                          </video>
                        ) : isDoc && mediaSrc ? (
                          <a href={mediaSrc} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 bg-neutral-100 dark:bg-navy-700 rounded-lg mb-1 hover:bg-neutral-200 dark:hover:bg-navy-600 transition-all">
                            <FileText size={20} className="text-blue-500 flex-shrink-0" />
                            <span className="text-xs font-bold text-navy-950 dark:text-white truncate">{msg.media?.filename || 'Documento'}</span>
                          </a>
                        ) : hasMedia && !mediaSrc ? (
                          <p className="text-xs text-neutral-400 italic py-1">Mídia não disponível</p>
                        ) : null}
                        {(isImage || isVideo) && caption && caption !== msg.body ? (
                          <p className="text-sm text-navy-950 dark:text-white whitespace-pre-wrap break-words">{caption}</p>
                        ) : !isSticker && ((!hasMedia) || (isDoc && mediaSrc)) && msg.body ? (
                          <p className="text-sm text-navy-950 dark:text-white whitespace-pre-wrap break-words">{msg.body}</p>
                        ) : (isImage || isVideo) && msg.body && !isSticker ? (
                          <p className="text-sm text-navy-950 dark:text-white whitespace-pre-wrap break-words">{msg.body}</p>
                        ) : null}
                        <p className={`text-[10px] mt-1 text-right ${msg.fromMe ? 'text-emerald-700/60 dark:text-emerald-400/60' : 'text-neutral-400'}`}>
                          {formatTime(msg.timestamp)}
                          <AckIcon ack={msg.ack} fromMe={msg.fromMe} />
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-3 border-t border-neutral-100 dark:border-navy-800 bg-neutral-50 dark:bg-navy-900">
              <form onSubmit={e => { e.preventDefault(); handleSendMessage(); }} className="flex items-center gap-2">
                <div className="relative">
                  <button type="button" onClick={() => { setShowFlowPicker(!showFlowPicker); if (!showFlowPicker && flows.length === 0) fetchFlows(); }}
                    className={`p-3 rounded-xl transition-all ${showFlowPicker ? 'bg-blue-500 text-white' : 'bg-white dark:bg-navy-950 text-neutral-400 hover:text-blue-500 border border-neutral-200 dark:border-navy-700'}`}
                    title="Enviar fluxo">
                    <Briefcase size={18} />
                  </button>
                  {showFlowPicker && (
                    <div className="absolute bottom-14 left-0 w-64 bg-white dark:bg-navy-900 border border-neutral-200 dark:border-navy-700 rounded-xl shadow-xl z-50 overflow-hidden">
                      <div className="p-3 border-b border-neutral-100 dark:border-navy-800">
                        <p className="text-xs font-black text-navy-950 dark:text-white uppercase tracking-widest">Enviar fluxo</p>
                      </div>
                      <div className="max-h-60 overflow-y-auto">
                        {birthdayConfig.active && birthdayConfig.messages.length > 0 && (
                          <button onClick={() => { handleSendFlow({ id: 'birthday', container_id: container?.id || '', name: 'Aniversário', messages: birthdayConfig.messages, target_tags: [], status: 'draft', created_at: '', updated_at: '' }, selectedChat?.id); setShowFlowPicker(false); }}
                            disabled={!!sendingFlowId}
                            className="w-full text-left px-4 py-3 hover:bg-neutral-50 dark:hover:bg-navy-800 transition-all flex items-center gap-3">
                            <Cake size={16} className="text-pink-500 flex-shrink-0" />
                            <div><p className="text-sm font-bold text-navy-950 dark:text-white">Aniversário</p><p className="text-[10px] text-neutral-400">{birthdayConfig.messages.length} msgs</p></div>
                          </button>
                        )}
                        {flows.map(flow => (
                          <button key={flow.id} onClick={() => { handleSendFlow(flow, selectedChat?.id); setShowFlowPicker(false); }}
                            disabled={!!sendingFlowId}
                            className="w-full text-left px-4 py-3 hover:bg-neutral-50 dark:hover:bg-navy-800 transition-all flex items-center gap-3 border-t border-neutral-100 dark:border-navy-800">
                            <Send size={14} className="text-blue-500 flex-shrink-0" />
                            <div><p className="text-sm font-bold text-navy-950 dark:text-white">{flow.name}</p><p className="text-[10px] text-neutral-400">{flow.messages.length} msgs</p></div>
                          </button>
                        ))}
                        {flows.length === 0 && !birthdayConfig.active && (
                          <p className="text-xs text-neutral-400 text-center py-4">Nenhum fluxo disponível</p>
                        )}
                      </div>
                      {sendingFlowId && (
                        <div className="p-3 border-t border-neutral-100 dark:border-navy-800 flex items-center gap-2 bg-blue-50 dark:bg-blue-900/20">
                          <Loader2 className="animate-spin text-blue-500" size={14} />
                          <span className="text-xs text-blue-600 font-bold">Enviando fluxo...</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <input value={messageText} onChange={e => setMessageText(e.target.value)} placeholder="Digite uma mensagem..." className="flex-1 bg-white dark:bg-navy-950 rounded-xl px-4 py-3 text-sm outline-none border border-neutral-200 dark:border-navy-700 dark:text-white" />
                <button type="submit" disabled={sendingMessage || !messageText.trim()} className="p-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-all disabled:opacity-50">
                  {sendingMessage ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
                </button>
              </form>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-neutral-50 dark:bg-navy-900/50">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto bg-neutral-100 dark:bg-navy-800 rounded-full flex items-center justify-center mb-4"><MessageSquare className="text-neutral-400" size={32} /></div>
              <p className="text-neutral-400 font-bold">Selecione uma conversa</p>
            </div>
          </div>
        )}
      </div>

      {/* ═══ RIGHT: Contact Info Panel ═══ */}
      {selectedChat && !isGroup(selectedChat) && (() => {
        const chatPhone = selectedChat.id.replace('@s.whatsapp.net', '').replace('@c.us', '');
        const crmContact = crmContacts.find(c =>
          c.phones?.some(p => {
            const digits = p.replace(/\D/g, '');
            if (!digits || digits.length < 10) return false;
            const normalized = digits.startsWith('55') ? digits : '55' + digits;
            return normalized === chatPhone || chatPhone === digits;
          })
        );

        const InfoRow = ({ icon: Icon, label, value }: { icon: any; label: string; value: string }) => (
          <div className="flex items-start gap-3 py-2.5 border-b border-neutral-50 dark:border-navy-800/50">
            <Icon size={14} className="text-neutral-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[9px] font-black text-slate-400 uppercase">{label}</p>
              <p className="text-sm font-bold text-navy-950 dark:text-white truncate">{value || '—'}</p>
            </div>
          </div>
        );

        const hasGeo = crmContact?.latitude && crmContact?.longitude;
        const mapUrl = hasGeo ? `https://maps.googleapis.com/maps/api/staticmap?center=${crmContact!.latitude},${crmContact!.longitude}&zoom=15&size=320x160&scale=2&markers=color:red%7C${crmContact!.latitude},${crmContact!.longitude}&key=${CONFIG.googleMapsKey}` : null;

        return (
          <div className="hidden lg:flex flex-col w-80 border-l border-neutral-200 dark:border-navy-800 bg-neutral-50 dark:bg-navy-900 overflow-y-auto">
            {/* Header */}
            <div className="p-6 border-b border-neutral-100 dark:border-navy-800 text-center">
              <div className="relative w-20 h-20 mx-auto mb-4">
                <div className="w-20 h-20 bg-neutral-200 dark:bg-navy-700 rounded-full flex items-center justify-center">
                  <UserIcon className="text-neutral-400" size={32} />
                </div>
                {(crmContact?.photo_url || selectedChat.picture) && (
                  <img src={crmContact?.photo_url || (selectedChat.picture!.startsWith('http') ? selectedChat.picture! : selectedChat.picture!.startsWith('data:') ? selectedChat.picture! : `data:image/jpeg;base64,${selectedChat.picture}`)} alt="" className="absolute inset-0 w-20 h-20 rounded-full object-cover" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                )}
              </div>
              <p className="font-black text-navy-950 dark:text-white text-lg">{crmContact?.name || formatChatName(selectedChat)}</p>
              <p className="text-xs text-neutral-400 font-mono mt-1">{chatPhone}</p>
              {crmContact ? (
                <>
                  <span className="inline-block mt-2 px-3 py-1 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 text-[9px] font-black uppercase tracking-widest rounded-full">{crmContact.type}</span>
                  <button onClick={() => onViewContact?.(crmContact.id)} className="mt-3 px-4 py-2 bg-navy-950 dark:bg-parliament-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 mx-auto hover:opacity-90 transition-all">
                    <Edit2 size={12} /> Editar
                  </button>
                  <div className="flex gap-1.5 mt-2 justify-center flex-wrap">
                    <button onClick={() => onCreateForContact?.('attendance', crmContact.id)} className="px-3 py-1.5 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 rounded-lg text-[9px] font-black uppercase hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-all">Atendimento</button>
                    <button onClick={() => onCreateForContact?.('task', crmContact.id)} className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 rounded-lg text-[9px] font-black uppercase hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-all">Tarefa</button>
                    <button onClick={() => onCreateForContact?.('document', crmContact.id)} className="px-3 py-1.5 bg-purple-50 dark:bg-purple-900/20 text-purple-600 rounded-lg text-[9px] font-black uppercase hover:bg-purple-100 dark:hover:bg-purple-900/40 transition-all">Documento</button>
                  </div>
                </>
              ) : (
                <button onClick={() => handleAddToCRM(selectedChat, chatPhone)} disabled={savingContactPhoto}
                  className="mt-3 px-4 py-2 bg-emerald-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 mx-auto hover:bg-emerald-700 transition-all disabled:opacity-60">
                  {savingContactPhoto ? <Loader2 size={12} className="animate-spin" /> : <UserPlus size={12} />} Adicionar ao CRM
                </button>
              )}
            </div>

            {/* Info fields */}
            <div className="p-4 space-y-0">
              <InfoRow icon={Phone} label="Telefone" value={formatPhone(chatPhone)} />
              <InfoRow icon={MessageSquare} label="Nome WhatsApp" value={selectedChat.name && !isMaskedName(selectedChat.name) ? selectedChat.name : ''} />
              {crmContact ? (
                <>
                  <InfoRow icon={Mail} label="E-mail" value={crmContact.emails?.[0] || ''} />
                  <InfoRow icon={Calendar} label="Nascimento" value={crmContact.birthdate ? new Date(crmContact.birthdate + 'T12:00:00').toLocaleDateString('pt-BR') : ''} />
                  <InfoRow icon={Briefcase} label="Profissão" value={crmContact.profession || ''} />
                  <InfoRow icon={MapPin} label="Cidade" value={crmContact.city || ''} />
                  <InfoRow icon={MapPin} label="Bairro" value={crmContact.neighborhood || ''} />
                  <InfoRow icon={UserIcon} label="Nível de Apoio" value={crmContact.support_level || ''} />
                </>
              ) : null}
            </div>

            {/* Tags */}
            {crmContact && (
              <div className="px-4 pb-4">
                <p className="text-[9px] font-black text-slate-400 uppercase mb-2 flex items-center gap-1"><Tag size={10} /> Tags</p>
                {crmContact.tags && crmContact.tags.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {crmContact.tags.map(tag => (
                      <span key={tag} className="px-2.5 py-1 bg-parliament-50 dark:bg-navy-800 text-parliament-600 dark:text-parliament-400 text-[10px] font-bold rounded-lg">{tag}</span>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-neutral-400">—</p>
                )}
              </div>
            )}

            {/* Observations */}
            {crmContact?.observations && (
              <div className="px-4 pb-4">
                <p className="text-[9px] font-black text-slate-400 uppercase mb-1">Observações</p>
                <p className="text-xs text-neutral-600 dark:text-slate-400 leading-relaxed">{crmContact.observations}</p>
              </div>
            )}

            {/* Map */}
            {mapUrl && (
              <div className="px-4 pb-4">
                <p className="text-[9px] font-black text-slate-400 uppercase mb-2 flex items-center gap-1"><MapPin size={10} /> Localização</p>
                <img src={mapUrl} alt="Mapa" className="w-full h-32 object-cover rounded-xl border border-neutral-200 dark:border-navy-700" />
              </div>
            )}

            {/* Related items */}
            {crmContact && (() => {
              const relTasks = allTasks.filter(t => t.contactid === crmContact.id);
              const relAtts = allAttendances.filter(a => a.contactid === crmContact.id);
              if (relTasks.length === 0 && relAtts.length === 0) return null;
              return (
                <div className="px-4 pb-4 space-y-3">
                  {relTasks.length > 0 && (
                    <div>
                      <p className="text-[9px] font-black text-slate-400 uppercase mb-1.5">Tarefas ({relTasks.length})</p>
                      <div className="space-y-1">
                        {relTasks.slice(0, 5).map(t => (
                          <div key={t.id} className="flex items-start gap-2 p-2 bg-neutral-100 dark:bg-navy-800 rounded-lg">
                            <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-1 ${t.status === 'Concluído' ? 'bg-emerald-500' : t.status === 'Em andamento' ? 'bg-blue-500' : 'bg-amber-500'}`} />
                            <div className="flex-1 min-w-0">
                              <p className="text-[11px] font-bold text-navy-950 dark:text-white leading-tight">{t.title}</p>
                              <p className="text-[9px] text-slate-400 mt-0.5">{t.status}{t.duedate ? ` • ${new Date(t.duedate + 'T12:00:00').toLocaleDateString('pt-BR')}` : ''}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {relAtts.length > 0 && (
                    <div>
                      <p className="text-[9px] font-black text-slate-400 uppercase mb-1.5">Atendimentos ({relAtts.length})</p>
                      <div className="space-y-1">
                        {relAtts.slice(0, 5).map(a => (
                          <div key={a.id} className="flex items-start gap-2 p-2 bg-neutral-100 dark:bg-navy-800 rounded-lg">
                            <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-1 ${a.status === 'Concluído' ? 'bg-emerald-500' : a.status === 'Em andamento' ? 'bg-blue-500' : 'bg-amber-500'}`} />
                            <div className="flex-1 min-w-0">
                              <p className="text-[11px] font-bold text-navy-950 dark:text-white leading-tight">{a.demand}</p>
                              <p className="text-[9px] text-slate-400 mt-0.5">{a.status}{a.created_at ? ` • ${new Date(a.created_at).toLocaleDateString('pt-BR')}` : ''}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

          </div>
        );
      })()}

      {/* ═══ RIGHT: Group Info Panel ═══ */}
      {selectedChat && isGroup(selectedChat) && (
        <div className="hidden lg:flex flex-col w-80 border-l border-neutral-200 dark:border-navy-800 bg-neutral-50 dark:bg-navy-900 overflow-y-auto">
          <div className="p-6 border-b border-neutral-100 dark:border-navy-800 text-center">
            <div className="w-20 h-20 mx-auto bg-neutral-200 dark:bg-navy-700 rounded-full flex items-center justify-center mb-4">
              <Users className="text-neutral-400" size={32} />
            </div>
            <p className="font-black text-navy-950 dark:text-white text-lg">{formatChatName(selectedChat)}</p>
            <p className="text-[10px] text-neutral-400 mt-1">Grupo</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppPanel;
